import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, useSearchParams, Link } from 'react-router-dom';
// Removed ResetPassword import - no longer needed
import About from './About';
import TopNavigation from './TopNavigation';
import GettingStarted from './GettingStarted';
import Book from './Book';
import Stats from './Stats';
import Archive from './Archive';
import Privacy from './Privacy';
import Cookies from './Cookies';
import Blog from './blog/Blog';
import BlogPost from './blog/BlogPost';
import CookieConsent from './components/CookieConsent';
import { Case, Proposal, VerifierResult, Being, Device, Hideout } from './types';
import { getRandomCase, loadCases, checkProposal, isCorrectSolution, renderStars, getAffinityEmoji, getCaseByDate, loadAllCases, enhanceClueWithIncidentValues } from './gameLogic';
// import { evaluateVerifier as genericEvaluateVerifier } from './gameLogic'; // Removed unused import
import { analytics } from './services/analytics';
import { ANALYTICS_CONFIG } from './config/analytics';
import { serverAnalytics } from './services/serverAnalytics';
import { GameStateService, GameState } from './services/gameState';
import { statsService } from './services/stats';
// import { cookieConsentService } from './services/cookieConsent';
// Removed Supabase import - no longer needed
import MailerLiteSignup from './components/MailerLiteSignup';
import { updatePageSEO } from './utils/seo';
import AdminAnalytics from './AdminAnalytics';
import RouteTracker from './components/RouteTracker';
import ClueRaiderBanner from './components/ClueRaiderBanner';
import './App.css';
import gettingStartedImgAlt from './fluxlogoa.png';
// import amazonBadge from './amazon.png';
// import book0 from './book0.png'; // Commented out - book banners are hidden

// Feature flags to temporarily disable in-progress UI
const SHARE_PANEL_UPDATES_ENABLED = false;
const SHARE_DEBUG_PANEL_VISIBLE = false;

type MainGameLayout = 'standard' | 'beta';

type MainGameProps = {
  layout?: MainGameLayout;
};

function MainGame({ layout = 'standard' }: MainGameProps) {
  const isBetaLayout = layout === 'beta';
  const [searchParams] = useSearchParams();
  // Responsive image height for desktop optimization
  const imageRef = useRef<HTMLImageElement | null>(null);
  const [imageMaxHeight, setImageMaxHeight] = useState<string>('400px');

  useEffect(() => {
    function updateImageHeight() {
      setImageMaxHeight(window.innerWidth >= 768 ? '500px' : '400px');
    }
    updateImageHeight();
    window.addEventListener('resize', updateImageHeight);
    return () => {
      window.removeEventListener('resize', updateImageHeight);
    };
  }, []);
  const isAdminMode = searchParams.get('mode') === 'admin';
  const requestedDate = searchParams.get('date');
  
  console.log('MainGame component rendering');
  const [currentCase, setCurrentCase] = useState<Case | null>(null);
  const [proposals, setProposals] = useState<Array<{ proposal: Proposal; results: VerifierResult[] }>>([]);
  const [currentProposal, setCurrentProposal] = useState<Proposal>({ being: '', device: '', hideout: '' });
  const [showSolution, setShowSolution] = useState(false);
  const [gameWon, setGameWon] = useState(false);
  const [crossedOffBeings, setCrossedOffBeings] = useState<string[]>([]);
  const [crossedOffDevices, setCrossedOffDevices] = useState<string[]>([]);
  const [crossedOffHideouts, setCrossedOffHideouts] = useState<string[]>([]);
  const caseSolvedRef = useRef<HTMLDivElement | null>(null);
  const revealCardRef = useRef<HTMLDivElement | null>(null);
  const beingsRef = useRef<HTMLDivElement | null>(null);
  const devicesRef = useRef<HTMLDivElement | null>(null);
  const hideoutsRef = useRef<HTMLDivElement | null>(null);
  const proposalsRef = useRef<HTMLDivElement | null>(null);
  const [showNewCaseModal, setShowNewCaseModal] = useState(false);
  const [dateInput, setDateInput] = useState('');
  const [dateError, setDateError] = useState('');

  const [availableDates, setAvailableDates] = useState<string[]>([]);
  // Add state for turn count and revealed clues
  const [turnCount, setTurnCount] = useState(0);
  const [revealedClues, setRevealedClues] = useState<Set<number>>(new Set());
  const [crossedOffClues, setCrossedOffClues] = useState<Set<number>>(new Set());
  // Track when clues were revealed to prevent accidental cross-out
  const [clueRevealTimes, setClueRevealTimes] = useState<Map<number, number>>(new Map());
  const [gameEnded, setGameEnded] = useState(false);
  const [showComponents, setShowComponents] = useState(false);
  const [showClassifiedView, setShowClassifiedView] = useState(!isAdminMode);
  const heroLogoSrc = gettingStartedImgAlt; // Always use standard view logo
  // Add state for dynamic story progression
  const [storyProgress, setStoryProgress] = useState(0);
  // Hints state
  const [showHints, setShowHints] = useState(false);
  const [currentHintLevel, setCurrentHintLevel] = useState(0);
  // Confirmation dialog state
  const [confirmationDialog, setConfirmationDialog] = useState<{
    show: boolean;
    message: string;
    onConfirm: () => void;
  }>({ show: false, message: '', onConfirm: () => {} });
  const [storySentences, setStorySentences] = useState<string[]>([]);
  // Mobile UI state for compact story panel
  const [isMobile, setIsMobile] = useState(false);
  const [mobileShowEarlier, setMobileShowEarlier] = useState(false);
  // Track case start time for duration
  const caseStartTimeRef = useRef<number | null>(null);
  const [solveDurationMinutes, setSolveDurationMinutes] = useState<number | null>(null);
  // Add selection state
  const [selectedBeings, setSelectedBeings] = useState<string[]>([]);
  const [selectedDevices, setSelectedDevices] = useState<string[]>([]);
  const [selectedHideouts, setSelectedHideouts] = useState<string[]>([]);
  // Add field memo state
  const [fieldMemoRevealed, setFieldMemoRevealed] = useState(false);
  // Temporary color scheme toggle for testing
  const [colorScheme] = useState(0); // 0=Professional, 1=Sophisticated Purple, 2=Case File
  // New game mode: no par scoring, single proposal attempt
  const [newGameMode, setNewGameMode] = useState(true);
  
  // Helper functions to get headline and description (using brief versions when available)
  const getHeadline = (caseData: Case | null) => {
    if (!caseData?.incident) return 'DRACULA\'S NIGHT SCHOOL TEACHES BATS TO READ EXIT SIGNS';
    return caseData.incident.brief_headline || caseData.incident.headline || 'DRACULA\'S NIGHT SCHOOL TEACHES BATS TO READ EXIT SIGNS';
  };

  const getDescription = (caseData: Case | null) => {
    if (!caseData?.incident) return 'No case description available.';
    return caseData.incident.brief_description || caseData.incident.description || 'No case description available.';
  };

  // Update SEO when currentCase changes
  useEffect(() => {
    if (currentCase) {
      const headline = getHeadline(currentCase);
      const description = getDescription(currentCase);
      const date = currentCase.incident?.date || '';
      const dateFormatted = date ? new Date(date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '';
      const title = dateFormatted 
        ? `${headline} - ${dateFormatted} - Dimension Flux`
        : `${headline} - Dimension Flux`;
      const seoDescription = description.length > 155 
        ? description.substring(0, 152) + '...'
        : description;
      updatePageSEO(title, seoDescription);
    } else {
      // Default SEO for main page
      updatePageSEO(
        'Dimension Flux – Daily Inter-Dimensional Logic Puzzle Game',
        'Daily inter-dimensional logic puzzles where folklore creatures invade with mysterious weapons. Solve cryptic cases before the rift spreads across reality.'
      );
    }
  }, [currentCase]);
  
  // Helper function to get color scheme classes
  const getColorScheme = () => {
    switch (colorScheme) {
      case 0: // Professional (Gray)
        return {
          button: 'bg-slate-600 hover:bg-slate-700',
          panel: 'bg-slate-100 border-slate-300',
          selected: 'bg-slate-600 text-white border-slate-500',
          navigation: 'bg-slate-600 hover:bg-slate-700',
          primary: 'bg-slate-600 hover:bg-slate-700',
          secondary: 'bg-slate-500 hover:bg-slate-600'
        };
      case 1: // Sophisticated Purple
        return {
          button: 'bg-purple-800 hover:bg-purple-900',
          panel: 'bg-purple-50 border-purple-200',
          selected: 'bg-purple-800 text-white border-purple-700',
          navigation: 'bg-purple-800 hover:bg-purple-900',
          primary: 'bg-purple-800 hover:bg-purple-900',
          secondary: 'bg-purple-700 hover:bg-purple-800'
        };
      case 2: // Case File (Navy)
        return {
          button: 'bg-slate-800 hover:bg-slate-900',
          panel: 'bg-blue-50 border-blue-200',
          selected: 'bg-slate-800 text-white border-slate-700',
          navigation: 'bg-slate-800 hover:bg-slate-900',
          primary: 'bg-slate-800 hover:bg-slate-900',
          secondary: 'bg-blue-700 hover:bg-blue-800'
        };
      default:
        return {
          button: 'bg-slate-600 hover:bg-slate-700',
          panel: 'bg-slate-100 border-slate-300',
          selected: 'bg-slate-600 text-white border-slate-500',
          navigation: 'bg-slate-600 hover:bg-slate-700',
          primary: 'bg-slate-600 hover:bg-slate-700',
          secondary: 'bg-slate-500 hover:bg-slate-600'
        };
    }
  };
  // 1. After pressing Submit Proposal or (Un)Eliminate Selected, disable both buttons and set their color to grey until a new selection is made.
  // Add a state variable to track if an action was just taken:
  const [actionJustTaken, setActionJustTaken] = useState(false);
  // Add at the top of MainGame, after useState declarations:
  const [showWinModal, setShowWinModal] = useState(false);
  const [showLoseModal, setShowLoseModal] = useState(false);
  // Ensure page starts at the top on initial load/refresh (helps mobile pull-to-refresh)
  useEffect(() => {
    try {
      window.scrollTo({ top: 0, behavior: 'auto' });
    } catch (e) {
      // no-op
    }
  }, []);
  const [freeProposalUsed, setFreeProposalUsed] = useState(false);
  const [failedProposals, setFailedProposals] = useState(0);
  const [clueOrder, setClueOrder] = useState<number[]>([]);
  const isLoadingRef = useRef(false);

  // Helper function to create sequential clue order (verifiers are already randomized in JSON)
  const createSequentialClueOrder = (verifierCount: number): number[] => {
    return Array.from({ length: verifierCount }, (_, i) => i);
  };

  // User state (simplified)
  const [user, setUser] = useState<any>(null);

  // Load saved game state on component mount
  useEffect(() => {
    console.log('=== LOADING SAVED GAME STATE ===');
    console.log('Checking for saved game state...');
    
    if (isAdminMode) {
      // Admin mode: Check for saved state
      const localStorageData = localStorage.getItem('dimension23_game_state_v2');
      console.log('localStorage data:', localStorageData);
      
      // Check if there's a saved state before calling loadGameState
      if (localStorageData) {
        try {
          const parsed = JSON.parse(localStorageData);
          console.log('Parsed localStorage data has lastSaved:', parsed.lastSaved);
        } catch (e) {
          console.log('Error parsing localStorage data:', e);
        }
      }
      
      const savedState = GameStateService.loadGameState();
      console.log('Saved state found:', !!savedState);
      if (savedState) {
        console.log('Loading saved game state, revealedClues:', savedState.revealedClues);
        console.log('Loading saved game state, clueOrder:', savedState.clueOrder);
        
        isLoadingRef.current = true;
        
        // Set all state at once to avoid triggering save effect prematurely
        const revealedCluesSet = new Set(savedState.revealedClues || []);
        console.log('Created revealedCluesSet:', Array.from(revealedCluesSet));
        
        setCurrentCase(savedState.currentCase);
        setProposals(savedState.proposals || []);
        setCurrentProposal(savedState.currentProposal || { being: '', device: '', hideout: '' });
        setShowSolution(savedState.showSolution || false);
        setGameWon(savedState.gameWon || false);
        setCrossedOffBeings(savedState.crossedOffBeings || []);
        setCrossedOffDevices(savedState.crossedOffDevices || []);
        setCrossedOffHideouts(savedState.crossedOffHideouts || []);
        setCrossedOffClues(new Set(savedState.crossedOffClues || []));
        setTurnCount(savedState.turnCount || 0);
        setRevealedClues(revealedCluesSet);
        setGameEnded(savedState.gameEnded || false);
        setFreeProposalUsed(savedState.freeProposalUsed || false);
        setNewGameMode(savedState.newGameMode !== undefined ? savedState.newGameMode : true);
        setFailedProposals(savedState.failedProposals || 0);
        setClueOrder(savedState.clueOrder || []);
        setMobileShowEarlier(savedState.mobileShowEarlier || false);
        
        // Reset modal states when loading game
        setShowWinModal(false);
        setShowLoseModal(false);
        
        // Track loaded game
        if (savedState.currentCase) {
          analytics.trackUserInteraction('game_loaded', {
            date: savedState.currentCase.incident?.date,
            case_id: savedState.currentCase.case_id,
            turn_count: savedState.turnCount
          });
        }
        
        // Reset the loading flag after a short delay to allow state to settle
        setTimeout(() => {
          isLoadingRef.current = false;
        }, 100);
      }
    }
  }, [isAdminMode]);

  // Simplified user initialization (no authentication)
  useEffect(() => {
    // Set a default user for now
    setUser({
      id: 'default-user',
      email: 'user@example.com',
      created_at: new Date().toISOString()
    });
  }, []);

  // Auto-reveal all clues when case is loaded
  useEffect(() => {
    if (currentCase && currentCase.verifiers) {
      const allClueIndices = new Set<number>();
      for (let i = 0; i < currentCase.verifiers.length; i++) {
        allClueIndices.add(i);
      }
      setRevealedClues(allClueIndices);
    }
  }, [currentCase]);

  // Save game state whenever it changes (both modes)
  useEffect(() => {
    if (currentCase && !gameEnded && !gameWon && !isLoadingRef.current) {
      const gameState: GameState = {
        currentCase,
        proposals,
        currentProposal,
        showSolution,
        gameWon,
        crossedOffBeings,
        crossedOffDevices,
        crossedOffHideouts,
        crossedOffClues: Array.from(crossedOffClues),
        turnCount,
        revealedClues: Array.from(revealedClues),
        fieldMemoRevealed,
        gameEnded,
        freeProposalUsed,
        failedProposals,
        clueOrder,
        mobileShowEarlier,
        newGameMode,
        lastSaved: new Date().toISOString()
      };
      console.log('Saving game state, revealedClues:', Array.from(revealedClues));
      console.log('Saving game state, clueOrder:', clueOrder);
      console.log('Saving game state, mode:', isAdminMode ? 'admin' : 'user');
      console.log('Saving game state, case date:', currentCase.incident?.date);
      GameStateService.saveGameState(gameState);
    }
  }, [
    isAdminMode, currentCase, proposals, currentProposal, showSolution, gameWon,
    crossedOffBeings, crossedOffDevices, crossedOffHideouts, crossedOffClues, turnCount,
    revealedClues, fieldMemoRevealed, gameEnded, freeProposalUsed, clueOrder, mobileShowEarlier,
    failedProposals, newGameMode
  ]);

  // Show modal when game ends or is won
  useEffect(() => {
    if (gameWon || (gameEnded && !gameWon)) {
      // setShowEndModal(true); // This line is removed
    }
  }, [gameWon, gameEnded]);

  // Show modal when game is won
  useEffect(() => {
    console.debug('gameWon changed:', gameWon);
    if (gameWon && !isLoadingRef.current) {
      setShowWinModal(true);
      console.debug('Setting showWinModal to true');
      if (caseStartTimeRef.current) {
        const elapsedMs = Date.now() - caseStartTimeRef.current;
        setSolveDurationMinutes(Math.max(1, Math.ceil(elapsedMs / 60000)));
      } else {
        setSolveDurationMinutes(null);
      }
      
      // Save the final won state
      if (currentCase) {
        const gameState: GameState = {
          currentCase,
          proposals,
          currentProposal,
          showSolution,
          gameWon: true,
          crossedOffBeings,
          crossedOffDevices,
          crossedOffHideouts,
          crossedOffClues: Array.from(crossedOffClues),
          turnCount,
          revealedClues: Array.from(revealedClues),
          fieldMemoRevealed,
          gameEnded: true,
          freeProposalUsed,
          failedProposals,
          clueOrder,
          mobileShowEarlier,
          newGameMode,
          lastSaved: new Date().toISOString()
        };
        console.log('Saving final won game state');
        GameStateService.saveGameState(gameState);
      }
    }
  }, [gameWon, currentCase, proposals, currentProposal, showSolution, crossedOffBeings, crossedOffDevices, crossedOffHideouts, crossedOffClues, turnCount, revealedClues, fieldMemoRevealed, freeProposalUsed, clueOrder, mobileShowEarlier, failedProposals, newGameMode]);

  // Show modal when game is lost (max moves reached and not won)
  useEffect(() => {
    if (gameEnded && !gameWon && !isLoadingRef.current) {
      setShowLoseModal(true);
      
      // Save the final lost state
      if (currentCase) {
        const gameState: GameState = {
          currentCase,
          proposals,
          currentProposal,
          showSolution,
          gameWon: false,
          crossedOffBeings,
          crossedOffDevices,
          crossedOffHideouts,
          crossedOffClues: Array.from(crossedOffClues),
          turnCount,
          revealedClues: Array.from(revealedClues),
          fieldMemoRevealed,
          gameEnded: true,
          freeProposalUsed,
          failedProposals,
          clueOrder,
          mobileShowEarlier,
          newGameMode,
          lastSaved: new Date().toISOString()
        };
        console.log('Saving final lost game state');
        GameStateService.saveGameState(gameState);
      }
    }
  }, [gameEnded, gameWon, currentCase, proposals, currentProposal, showSolution, crossedOffBeings, crossedOffDevices, crossedOffHideouts, crossedOffClues, turnCount, revealedClues, fieldMemoRevealed, freeProposalUsed, clueOrder, mobileShowEarlier, failedProposals, newGameMode]);

  const calculateScores = useCallback((caseData: Case | null) => {
    // All code in this function is now commented out or unused, so remove all unused variable declarations
  }, []);

  const startNewGame = useCallback(() => {
    console.log('🔥 startNewGame called 🔥');
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    // Load current day's case instead of random
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const todayString = `${year}${month}${day}`;
    console.log('Today\'s date (local):', todayString);
    // Check if there's an exact match for today's date
    console.log('🔍 App.tsx calling loadAllCases...');
    const allCases = loadAllCases();
    console.log('📊 App.tsx loaded cases:', allCases.length);
    const exactTodayCase = allCases.find(c => {
      const caseDate = c.incident?.date;
      if (!caseDate) return false;
      
      // Normalize case date for comparison
      let normalizedCaseDate: string;
      if (/^\d{8}$/.test(caseDate)) {
        // Convert YYYYMMDD to YYYY-MM-DD
        const year = caseDate.substring(0, 4);
        const month = caseDate.substring(4, 6);
        const day = caseDate.substring(6, 8);
        normalizedCaseDate = `${year}-${month}-${day}`;
      } else {
        normalizedCaseDate = caseDate;
      }
      
      // Convert today's date to YYYY-MM-DD format for comparison
      const todayFormatted = `${year}-${month}-${day}`;
      
      return normalizedCaseDate === todayFormatted || caseDate === todayString;
    });
    
    console.log('Exact today case found:', exactTodayCase ? 'Yes' : 'No');
    if (exactTodayCase) {
      console.log('Exact today case date:', exactTodayCase.incident?.date);
    }
    
    // Debug: Check what cases are available
    console.log('Total cases available:', allCases.length);
    const availableDates = allCases.map(c => c.incident?.date).filter(Boolean);
    console.log('Available dates:', availableDates.slice(0, 10)); // Show first 10 dates
    
    if (exactTodayCase) {
      setCurrentCase(exactTodayCase);
      setProposals([]);
      setCurrentProposal({ being: '', device: '', hideout: '' });
      setShowSolution(false);
      setGameWon(false);

      setCrossedOffBeings([]);
      setCrossedOffDevices([]);
      setCrossedOffHideouts([]);
      setTurnCount(0);
      setRevealedClues(new Set());
      setClueRevealTimes(new Map());
      setFieldMemoRevealed(false);
      setGameEnded(false);
      setFreeProposalUsed(false);
      setFailedProposals(0);
      // Use sequential clue order (verifiers already randomized in JSON)
      setClueOrder(createSequentialClueOrder(exactTodayCase.verifiers.length));
      calculateScores(exactTodayCase);
      setShowNewCaseModal(false);
      
      // Clear saved game state
      GameStateService.clearGameState();
      
      // Track game start
      analytics.trackGameStart(exactTodayCase.incident?.date || 'unknown', exactTodayCase.case_id);
    } else {
      // Fallback to random case if no current day case
      const newCase = getRandomCase();
      if (newCase) {
        setCurrentCase(newCase);
        setProposals([]);
        setCurrentProposal({ being: '', device: '', hideout: '' });
        setShowSolution(false);
        setGameWon(false);

        setCrossedOffBeings([]);
        setCrossedOffDevices([]);
        setCrossedOffHideouts([]);
        setTurnCount(0);
        setRevealedClues(new Set());
        setClueRevealTimes(new Map());
        setGameEnded(false);
        setFreeProposalUsed(false);
        setFailedProposals(0);
        // Use sequential clue order (verifiers already randomized in JSON)
        setClueOrder(createSequentialClueOrder(newCase.verifiers.length));
        calculateScores(newCase);
        setShowNewCaseModal(false);
        
        // Clear saved game state
        GameStateService.clearGameState();
        
        // Track game start
        analytics.trackGameStart(newCase.incident?.date || 'unknown', newCase.case_id);
      }
    }
  }, [calculateScores]);



  const startNewGameByDate = useCallback((dateString: string) => {
    console.log('startNewGameByDate called with:', dateString);
    
    // Convert DD/MM/YYYY format to YYYYMMDD format
    let convertedDateString = dateString;
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
      // Convert DD/MM/YYYY to YYYYMMDD
      const parts = dateString.split('/');
      const day = parts[0];
      const month = parts[1];
      const year = parts[2];
      convertedDateString = `${year}${month}${day}`;
      console.log('Converted date from DD/MM/YYYY to YYYYMMDD:', dateString, '->', convertedDateString);
    }
    
    const newCase = getCaseByDate(convertedDateString);
    console.log('getCaseByDate returned:', newCase ? 'Case found' : 'No case found');
    if (newCase) {
      console.log('Case found, checking date match. Case date:', newCase.incident.date, 'Requested date:', convertedDateString);
      // Check if the case actually matches the requested date
      const requestedDate = convertedDateString.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3');
      const caseDate = newCase.incident.date;
      
      console.log('Date comparison - requestedDate:', requestedDate, 'caseDate:', caseDate);
      
      // Normalize both dates for comparison
      const normalizeDate = (dateStr: string) => {
        if (/^\d{8}$/.test(dateStr)) {
          // Convert YYYYMMDD to YYYY-MM-DD
          const year = dateStr.substring(0, 4);
          const month = dateStr.substring(4, 6);
          const day = dateStr.substring(6, 8);
          return `${year}-${month}-${day}`;
        }
        return dateStr;
      };
      
      const normalizedCaseDate = normalizeDate(caseDate);
      const normalizedRequestedDate = normalizeDate(requestedDate);
      const normalizedDateString = normalizeDate(convertedDateString);
      
      console.log('Normalized comparison - caseDate:', normalizedCaseDate, 'requestedDate:', normalizedRequestedDate, 'dateString:', normalizedDateString);
      
      if (normalizedCaseDate !== normalizedRequestedDate && normalizedCaseDate !== normalizedDateString) {
        // Try to find the next available case with the correct date
        const allCases = loadAllCases();
        const correctCase = allCases.find((c: Case) => {
          const cDate = c.incident.date;
          return cDate === requestedDate || cDate === convertedDateString;
        });
        
        if (correctCase) {
          window.scrollTo({ top: 0, behavior: 'smooth' });
          setCurrentCase(correctCase);
          setProposals([]);
          setCurrentProposal({ being: '', device: '', hideout: '' });
          setShowSolution(false);
          setGameWon(false);
          setCrossedOffBeings([]);
          setCrossedOffDevices([]);
          setCrossedOffHideouts([]);
          setTurnCount(0);
          setRevealedClues(new Set());
          setGameEnded(false);
          setFreeProposalUsed(false);
          setFailedProposals(0);
          // Use sequential clue order (verifiers already randomized in JSON)
          setClueOrder(createSequentialClueOrder(correctCase.verifiers.length));
          calculateScores(correctCase);
          setShowNewCaseModal(false);
          setDateInput('');
          setDateError('');
          
          // Clear saved game state
          GameStateService.clearGameState();
          
          // Track game start
          analytics.trackGameStart(correctCase.incident?.date || 'unknown', correctCase.case_id);
        } else {
          setDateError('No case found for that date.');
        }
      } else {
        // Case date matches, proceed normally
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setCurrentCase(newCase);
        setProposals([]);
        setCurrentProposal({ being: '', device: '', hideout: '' });
        setShowSolution(false);
        setGameWon(false);
        setCrossedOffBeings([]);
        setCrossedOffDevices([]);
        setCrossedOffHideouts([]);
        setTurnCount(0);
        setRevealedClues(new Set());
        setClueRevealTimes(new Map());
        setGameEnded(false);
        setFreeProposalUsed(false);
        setFailedProposals(0);
        // Use sequential clue order (verifiers already randomized in JSON)
        setClueOrder(createSequentialClueOrder(newCase.verifiers.length));
        calculateScores(newCase);
        setShowNewCaseModal(false);
        setDateInput('');
        setDateError('');
        
        // Clear saved game state
        GameStateService.clearGameState();
        
        // Track game start
        analytics.trackGameStart(newCase.incident?.date || 'unknown', newCase.case_id);
      }
    } else {
      setDateError('No case found for that date.');
    }
  }, [calculateScores]);

  const startRandomGame = useCallback(() => {
    const allCases = loadCases();
    if (allCases.length === 0) {
      setDateError('No cases available.');
      return;
    }
    
    const randomIndex = Math.floor(Math.random() * allCases.length);
    const randomCase = allCases[randomIndex];
    
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setCurrentCase(randomCase);
    setProposals([]);
    setCurrentProposal({ being: '', device: '', hideout: '' });
    setShowSolution(false);
    setGameWon(false);

    setCrossedOffBeings([]);
    setCrossedOffDevices([]);
    setCrossedOffHideouts([]);
    setTurnCount(0);
    setRevealedClues(new Set());
    setClueRevealTimes(new Map());
    setFieldMemoRevealed(false);
    setGameEnded(false);
    setFreeProposalUsed(false);
    setFailedProposals(0);
          // Use sequential clue order (verifiers already randomized in JSON)
      setClueOrder(createSequentialClueOrder(randomCase.verifiers.length));
    calculateScores(randomCase);
    setShowNewCaseModal(false);
    setDateInput('');
    setDateError('');
    
    // Clear saved game state
    GameStateService.clearGameState();
    
    // Track game start
    analytics.trackGameStart(randomCase.incident?.date || 'unknown', randomCase.case_id);
  }, [calculateScores]);



  const loadAvailableDates = useCallback(() => {
    const allCases = loadCases();
    // Sort dates chronologically by converting to Date objects first
    const dates = Array.from(new Set(allCases.map(c => c.incident.date)))
      .filter(date => date && date !== undefined && date !== null) // Filter out undefined/null dates
      .sort((a, b) => {
        // Convert both dates to Date objects for proper comparison
        const dateA = new Date(a.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3'));
        const dateB = new Date(b.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3'));
        return dateA.getTime() - dateB.getTime();
      });
    console.log('Available dates:', dates); // Debug: log available dates
    setAvailableDates(dates);
  }, []);

  useEffect(() => {
    // Only start a new game if there's no saved state (admin mode only)
    if (isAdminMode && !GameStateService.hasSavedGame()) {
      startNewGame();
    }
    loadAvailableDates(); // Load available dates on component initialization
    const handleResize = () => {
      // setIsMobile(window.innerWidth < 900); // This line is removed
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isAdminMode, startNewGame, loadAvailableDates]);

  // Handle date parameter from URL (for Archive page)
  useEffect(() => {
    if (requestedDate && !isAdminMode) {
      console.log('Date parameter detected:', requestedDate);
      startNewGameByDate(requestedDate);
    }
  }, [requestedDate, isAdminMode, startNewGameByDate]);

  // User mode initialization - ensure it runs after admin mode logic
  // Only run if no date parameter is requested
  useEffect(() => {
    if (!isAdminMode && !currentCase && !requestedDate) {
      console.log('User mode: Initializing current day case');
      // User mode: Load current day's case
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const todayString = `${year}${month}${day}`;
      console.log('User mode: Today\'s date (local):', todayString);
      // Check if there's an exact match for today's date
      const allCases = loadAllCases();
      const exactTodayCase = allCases.find(c => {
        const caseDate = c.incident?.date;
        if (!caseDate) return false;
        
        // Normalize case date for comparison
        let normalizedCaseDate: string;
        if (/^\d{8}$/.test(caseDate)) {
          // Convert YYYYMMDD to YYYY-MM-DD
          const year = caseDate.substring(0, 4);
          const month = caseDate.substring(4, 6);
          const day = caseDate.substring(6, 8);
          normalizedCaseDate = `${year}-${month}-${day}`;
        } else {
          normalizedCaseDate = caseDate;
        }
        
        // Convert today's date to YYYY-MM-DD format for comparison
        const todayFormatted = `${year}-${month}-${day}`;
        
        return normalizedCaseDate === todayFormatted || caseDate === todayString;
      });
      
      const todayFormatted = `${year}-${month}-${day}`;

      // If no puzzle for today, cycle through the puzzle set by day index (day 0 = first puzzle, day 1 = second, ... wrap)
      let caseToUse: typeof exactTodayCase = exactTodayCase;
      if (!caseToUse && allCases.length > 0) {
        const sortedByDate = [...allCases].sort((a, b) => {
          const da = a.incident?.date ?? '';
          const db = b.incident?.date ?? '';
          return String(da).localeCompare(String(db));
        });
        const n = sortedByDate.length;
        const parseCaseDate = (dateStr: string): Date => {
          const s = String(dateStr).replace(/-/g, '');
          const y = parseInt(s.slice(0, 4), 10);
          const m = parseInt(s.slice(4, 6), 10) - 1;
          const d = parseInt(s.slice(6, 8), 10);
          return new Date(y, m, d);
        };
        const firstDate = parseCaseDate(sortedByDate[0].incident?.date ?? '');
        const todayDate = new Date(year, parseInt(month, 10) - 1, parseInt(day, 10));
        const daysDiff = Math.floor((todayDate.getTime() - firstDate.getTime()) / (24 * 60 * 60 * 1000));
        const index = ((daysDiff % n) + n) % n;
        const cycledCase = sortedByDate[index];
        caseToUse = {
          ...cycledCase,
          case_id: todayString,
          incident: cycledCase.incident
            ? { ...cycledCase.incident, date: todayFormatted }
            : { date: todayFormatted, type: 'Supernatural' as const, description: '', headline: '', brief_headline: undefined, brief_description: undefined },
        };
        console.log('User mode: No case for today; cycling puzzle set: index', index, 'of', n, '(', cycledCase.incident?.date, ') -> displayed as', todayFormatted);
      }

      console.log('User mode: Exact today case found:', exactTodayCase ? 'Yes' : 'No');
      if (exactTodayCase) {
        console.log('User mode: Exact today case date:', exactTodayCase.incident?.date);
      }

      // Debug: Check what cases are available
      console.log('User mode: Total cases available:', allCases.length);
      const availableDates = allCases.map(c => c.incident?.date).filter(Boolean);
      console.log('User mode: Available dates:', availableDates.slice(0, 10)); // Show first 10 dates

      if (caseToUse) {
          console.log('User mode: Found current day case:', caseToUse.incident?.date);

          // Check if there's a saved state for today's case
          const savedState = GameStateService.loadGameState();
          const savedCaseDate = savedState?.currentCase?.incident?.date;

          console.log('User mode: Saved state case date:', savedCaseDate);

          // Normalize both dates for comparison
          const normalizeDate = (dateStr: string) => {
            if (!dateStr) return null;
            if (/^\d{8}$/.test(dateStr)) {
              // Convert YYYYMMDD to YYYY-MM-DD
              const year = dateStr.substring(0, 4);
              const month = dateStr.substring(4, 6);
              const day = dateStr.substring(6, 8);
              return `${year}-${month}-${day}`;
            }
            return dateStr;
          };

          const normalizedToday = normalizeDate(todayString);
          const normalizedSaved = savedCaseDate ? normalizeDate(savedCaseDate) : null;
          const normalizedCurrentCase = caseToUse.incident?.date ? normalizeDate(caseToUse.incident.date) : null;

        console.log('User mode debug:');
        console.log('  todayString:', todayString);
        console.log('  savedCaseDate:', savedCaseDate);
                  console.log('  currentCaseDate:', caseToUse.incident?.date);
        console.log('  normalizedToday:', normalizedToday);
        console.log('  normalizedSaved:', normalizedSaved);
        console.log('  normalizedCurrentCase:', normalizedCurrentCase);
        console.log('  savedState exists:', !!savedState);
        console.log('  saved vs today match:', normalizedSaved === normalizedToday);
        console.log('  saved vs current case match:', normalizedSaved === normalizedCurrentCase);

        // Check if saved state matches either today's date or the current case date
        if (savedState && (normalizedSaved === normalizedToday || normalizedSaved === normalizedCurrentCase)) {
          // Load saved state for today's case
          console.log('Loading saved state for today\'s case');
          isLoadingRef.current = true;

          const revealedCluesSet = new Set(savedState.revealedClues || []);
          console.log('Created revealedCluesSet:', Array.from(revealedCluesSet));

          setCurrentCase(savedState.currentCase);
          setProposals(savedState.proposals || []);
          setCurrentProposal(savedState.currentProposal || { being: '', device: '', hideout: '' });
          setShowSolution(savedState.showSolution || false);
          setGameWon(savedState.gameWon || false);
          setCrossedOffBeings(savedState.crossedOffBeings || []);
          setCrossedOffDevices(savedState.crossedOffDevices || []);
          setCrossedOffHideouts(savedState.crossedOffHideouts || []);
          setCrossedOffClues(new Set(savedState.crossedOffClues || []));
          setTurnCount(savedState.turnCount || 0);
          setRevealedClues(revealedCluesSet);
          setGameEnded(savedState.gameEnded || false);
          setFreeProposalUsed(savedState.freeProposalUsed || false);
          setClueOrder(savedState.clueOrder || []);
          setMobileShowEarlier(savedState.mobileShowEarlier || false);

          // Track loaded game
          if (savedState.currentCase) {
            analytics.trackUserInteraction('game_loaded', {
              date: savedState.currentCase.incident?.date,
              case_id: savedState.currentCase.case_id,
              turn_count: savedState.turnCount
            });
          }

          // Reset the loading flag after a short delay
          setTimeout(() => {
            isLoadingRef.current = false;
          }, 100);
        } else {
          // Start fresh with today's case
          console.log('User mode: Starting fresh with today\'s case');
          setCurrentCase(caseToUse);
          setProposals([]);
          setCurrentProposal({ being: '', device: '', hideout: '' });
          setShowSolution(false);
          setGameWon(false);
          setCrossedOffBeings([]);
          setCrossedOffDevices([]);
          setCrossedOffHideouts([]);
          setTurnCount(0);
          setRevealedClues(new Set());
          setGameEnded(false);
          setFreeProposalUsed(false);
          setFailedProposals(0);
          setClueOrder(createSequentialClueOrder(caseToUse.verifiers.length));
        }
      } else {
        // No cases at all in the dataset
        console.log('No case found for today (no cases in dataset):', todayString);
      }
    }
  }, [isAdminMode, currentCase, requestedDate]);

  // Load available dates when modal opens
  useEffect(() => {
    if (showNewCaseModal) {
      loadAvailableDates();
    }
  }, [showNewCaseModal, loadAvailableDates]);

  // Always scroll CASE SOLVED box into view when game is won
  useEffect(() => {
    if (gameWon && caseSolvedRef.current) {
      caseSolvedRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [gameWon]);

  // Initialize story sentences when case loads
  useEffect(() => {
    const description = getDescription(currentCase);
    if (description && description !== 'No case description available.') {
      const sentences = description
        .split('.')
        .filter(s => s.trim().length > 0)
        .map(s => s.trim() + '.')
        .map(s => s.replace(/^FIELD MEMO — /i, '')); // Remove FIELD MEMO prefix
      setStorySentences(sentences);
      setStoryProgress(0); // Reset to first sentence
      setMobileShowEarlier(false); // collapse earlier on new case
      // Set start time when a case loads
      caseStartTimeRef.current = Date.now();
      setSolveDurationMinutes(null);
    }
  }, [currentCase]);

  // Detect mobile viewport for responsive behavior
  useEffect(() => {
    const updateIsMobile = () => setIsMobile(window.innerWidth < 640); // Tailwind 'sm'
    updateIsMobile();
    window.addEventListener('resize', updateIsMobile);
    return () => window.removeEventListener('resize', updateIsMobile);
  }, []);

  // Calculate difficulty based on clues and types
  const calculateDifficulty = (verifiers: any[]) => {
    const totalClues = verifiers.length;
    // Exclude incident verifiers (IVI*) from intermediate count - they're used at all difficulty levels
    const intermediateCount = verifiers.filter(v => v.ID?.startsWith('IV') && !v.ID?.startsWith('IVI')).length;
    
    if (totalClues <= 4 && intermediateCount === 0) return 'EASY';
    if (totalClues <= 5 && intermediateCount <= 1) return 'MEDIUM'; 
    if (totalClues <= 5 && intermediateCount <= 3) return 'HARD';
    return 'EXPERT';
  };



  const handleRevealSolution = () => {
    setShowSolution(true);
    
    // Track solution reveal
    if (currentCase) {
      analytics.trackSolutionReveal(currentCase.incident?.date || 'unknown', currentCase.case_id);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleShareGame = useCallback(async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'Dimension Flux — Daily Logic Case',
          text: 'Can you solve today\'s breach before reality collapses? Join me on Dimension Flux.',
          url: window.location.href
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        alert('Link copied to clipboard!');
      }
    } catch (error) {
      console.error('Share failed:', error);
    }
  }, []);

  const caseImageSrc = getCaseImageSrc();
  const [showImageMode, setShowImageMode] = useState(false);
  const solvedDurationLabel = solveDurationMinutes !== null
    ? `Under ${solveDurationMinutes} minute${solveDurationMinutes === 1 ? '' : 's'}`
    : 'Under n minutes';
  const shareCardUrl = 'https://dimensionflux.com';
  const shareCardDate = (() => {
    const caseDateValue = currentCase?.incident?.date || currentCase?.date;
    return caseDateValue ? formatDateWithDay(caseDateValue) : 'Date Unknown';
  })();
  const shareDifficultyValue = currentCase?.verifiers ? calculateDifficulty(currentCase.verifiers) : '—';
  const [showShareSheet, setShowShareSheet] = useState(false);
  const [includeShareUrl, setIncludeShareUrl] = useState(true);
  const ShareSheet = () => {
    if (!SHARE_PANEL_UPDATES_ENABLED) return null;
    if (!showShareSheet || !currentCase) return null;
    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-70 p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5 relative border border-purple-200">
          <button
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
            onClick={() => setShowShareSheet(false)}
            aria-label="Close share sheet"
          >
            ✕
          </button>
          <h3 className="text-lg font-oswald text-gray-900 uppercase tracking-wide mb-3 text-center">
            Share Result
          </h3>
          <div className="mb-4">
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 font-oswald">
              <input
                type="checkbox"
                checked={includeShareUrl}
                onChange={(e) => setIncludeShareUrl(e.target.checked)}
                className="w-4 h-4 text-purple-600 border-gray-300 rounded"
              />
              Include dimensionflux.com link
            </label>
          </div>
          <div className="space-y-2">
            <button
              onClick={async () => {
                await handleCopyShareText();
                setShowShareSheet(false);
              }}
              className="w-full px-4 py-3 rounded-xl border border-gray-300 text-gray-800 font-oswald text-sm uppercase tracking-wide hover:bg-gray-50 transition-colors"
            >
              Copy Text
            </button>
            <button
              onClick={async () => {
                await handleShareTextOnly();
                setShowShareSheet(false);
              }}
              className="w-full px-4 py-3 rounded-xl bg-gray-900 text-white font-oswald text-sm uppercase tracking-wide hover:bg-black transition-colors"
            >
              Share Text
            </button>
            <button
              onClick={async () => {
                await handleShareImage();
                setShowShareSheet(false);
              }}
              className="w-full px-4 py-3 rounded-xl bg-purple-700 text-white font-oswald text-sm uppercase tracking-wide hover:bg-purple-800 transition-colors"
            >
              Share Image
            </button>
          </div>
        </div>
      </div>
    );
  };

  const handleShareResult = () => {
    if (!currentCase) return;
    
    let performanceText = '';
    if (!newGameMode) {
    const par = currentCase.verifiers.length + 1;
    const difference = turnCount - par;
    
    if (difference === 0) {
      performanceText = 'PAR!';
    } else if (difference < 0) {
      performanceText = `${Math.abs(difference)} under par`;
    } else {
      performanceText = `${difference} over par`;
      }
    }
    
    // Compute elapsed time since case start - use floor instead of ceil to match display
    let timeText = '';
    if (caseStartTimeRef.current) {
      const elapsedMs = Date.now() - caseStartTimeRef.current;
      const elapsedMinutes = Math.max(1, Math.floor(elapsedMs / 60000) + 1);
      timeText = `in less than ${elapsedMinutes} minute${elapsedMinutes === 1 ? '' : 's'}`;
    }
    
    // Get case date for the share message (use actual case date, not today)
    const caseDate = currentCase.incident?.date || currentCase.date;
    const caseDateFormatted = caseDate ? formatDateWithDay(caseDate) : formatDateWithDay(new Date().toISOString().split('T')[0]);
    
    const shareText = newGameMode 
      ? `🔍 DIMENSION FLUX - ${caseDateFormatted} 🔍\n\nI solved the case "${getHeadline(currentCase)}" ${timeText}! 🕵️‍♂️\n\nSolve tomorrow's inter-dimensional mystery at:`
      : `🔍 DIMENSION FLUX - ${caseDateFormatted} 🔍\n\nI solved the case "${getHeadline(currentCase)}" in ${turnCount} moves (${performanceText}) ${timeText}! 🕵️‍♂️\n\nSolve tomorrow's inter-dimensional mystery at:`;
    const shareUrl = window.location.href;
    
    const shareData = {
              title: 'Dimension Flux - Case Solved!',
      text: shareText,
      url: shareUrl
    };
    
    // Track share action
    analytics.trackShare('case_solved', currentCase.incident?.date, currentCase.case_id);
    
    // Try native sharing first
    if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
      navigator.share(shareData);
    } else {
      // Fallback: copy to clipboard
      const fullShareText = `${shareText} ${shareUrl}`;
      
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(fullShareText).catch(() => {
          // Fallback for older browsers
          const textArea = document.createElement('textarea');
          textArea.value = fullShareText;
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
        });
      } else {
        // Fallback for browsers without clipboard API
        const textArea = document.createElement('textarea');
        textArea.value = fullShareText;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
    }
  };

  const buildShareText = useCallback(
    (includeUrlOverride?: boolean) => {
      if (!currentCase) return '';
      const headline = decodeMojibake(getHeadline(currentCase));
      const parts = [
        `🔍 DIMENSION FLUX – ${shareCardDate} 🔍`,
        '',
        `I solved the case "${headline}" ${solvedDurationLabel.toLowerCase()}!`,
        ''
      ];
      if (includeUrlOverride ?? includeShareUrl) {
        parts.push(`Play at: ${shareCardUrl}`);
      }
      return parts.join('\n');
    },
    [currentCase, shareCardDate, solvedDurationLabel, includeShareUrl, shareCardUrl]
  );

  const handleCopyShareText = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(buildShareText());
      alert('Copied! Paste it anywhere to share.');
    } catch (err) {
      console.error('Copy failed', err);
      try {
        const text = buildShareText();
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        alert('Copied! Paste it anywhere to share.');
      } catch (fallbackErr) {
        console.error('Fallback copy failed', fallbackErr);
        alert('Unable to copy right now.');
      }
    }
  }, [buildShareText]);

  const handleShareTextOnly = useCallback(async () => {
    if (!currentCase) return;
    const text = buildShareText();
    const shareData: ShareData = {
      title: 'Dimension Flux – Case Solved',
      text
    };
    if (includeShareUrl) {
      shareData.url = shareCardUrl;
    }
    analytics.trackShare('case_solved', currentCase.incident?.date, currentCase.case_id);
    try {
      if (
        navigator.share &&
        (typeof navigator.canShare !== 'function' || navigator.canShare({ text: shareData.text }))
      ) {
        await navigator.share(shareData);
        return;
      }
      throw new Error('Share API not available');
    } catch (err) {
      console.warn('Text share fell back to copy', err);
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(text);
        } else {
          const textArea = document.createElement('textarea');
          textArea.value = text;
          textArea.style.position = 'fixed';
          textArea.style.left = '-9999px';
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
        }
        alert('Browser cannot share directly. Copied text instead!');
      } catch (copyErr) {
        console.error('Copy fallback failed', copyErr);
        alert('Unable to share text right now.');
      }
    }
  }, [currentCase, includeShareUrl, buildShareText, shareCardUrl]);

  const buildShareImageUrl = useCallback(() => {
    if (typeof window === 'undefined' || !currentCase) return '';
    const headline = decodeMojibake(getHeadline(currentCase));
    
    // Convert relative paths to absolute URLs
    const getAbsoluteUrl = (src: string | null): string | null => {
      if (!src) return null;
      try {
        // If it's already an absolute URL, return as-is
        if (src.startsWith('http://') || src.startsWith('https://')) {
          return src;
        }
        // If it starts with /, it's already a path from origin
        if (src.startsWith('/')) {
          return `${window.location.origin}${src}`;
        }
        // Otherwise, create a URL relative to origin
        return new URL(src, window.location.origin).toString();
      } catch (err) {
        console.warn('Failed to convert image URL:', src, err);
        return null;
      }
    };
    
    const absoluteImage = getAbsoluteUrl(caseImageSrc);
    const logoUrl = getAbsoluteUrl(heroLogoSrc);
    
    const payload = {
      date: shareCardDate,
      difficulty: shareDifficultyValue,
      headline,
      solvedText: solvedDurationLabel,
      imageUrl: absoluteImage,
      logoUrl: logoUrl
    };
    
    try {
      const jsonString = JSON.stringify(payload);
      const encoded = encodeURIComponent(
        btoa(unescape(encodeURIComponent(jsonString)))
      );
      // Add cache buster to ensure fresh images
      const cacheBuster = Date.now();
      return `${window.location.origin}/.netlify/functions/share-card?payload=${encoded}&_=${cacheBuster}`;
    } catch (err) {
      console.error('Failed to build share image URL:', err);
      return '';
    }
  }, [currentCase, shareCardDate, shareDifficultyValue, solvedDurationLabel, caseImageSrc, heroLogoSrc]);

  const handleShareImage = useCallback(async () => {
    if (!currentCase) return;
    const shareUrl = buildShareImageUrl();
    if (!shareUrl) {
      alert('Unable to generate share image right now.');
      return;
    }
    
    analytics.trackShare('case_solved', currentCase.incident?.date, currentCase.case_id);
    
    const shareText = includeShareUrl ? `${buildShareText()} ${shareCardUrl}` : buildShareText();
    const shareTitle = 'Dimension Flux – Case Solved';
    const caseDateRaw = currentCase.incident?.date || currentCase.date || 'case';
    const fileName = `dimensionflux-${caseDateRaw}.png`;
    
    // Log for debugging
    console.log('Starting share, navigator.share exists:', !!navigator.share, typeof navigator.share);
    
    try {
      let shared = false;
      
      // Try to share as a file first (best UX on modern mobile)
      // Check for file sharing support - be lenient
      if (navigator.share && typeof File !== 'undefined') {
        console.log('Attempting file share');
        try {
          const response = await fetch(shareUrl);
          
          if (!response.ok) {
            const text = await response.text();
            console.error('Share image fetch failed:', response.status, text.substring(0, 200));
            throw new Error(`Image fetch failed: ${response.status} ${response.statusText}`);
          }
          
          const contentType = response.headers.get('content-type') || '';
          const blob = await response.blob();
          
          // Check if we got HTML instead of an image
          if (contentType.includes('text/html') || blob.type.includes('text/html')) {
            const htmlPreview = await blob.text();
            console.error('Got HTML instead of image. Response:', htmlPreview.substring(0, 500));
            
            // Check if we're in local development
            const isLocalDev = window.location.hostname === 'localhost' || 
                              window.location.hostname === '127.0.0.1' ||
                              /^192\.168\./.test(window.location.hostname) ||
                              /^10\./.test(window.location.hostname);
            
            if (isLocalDev) {
              alert('⚠️ Netlify function not available!\n\n' +
                    'The function URL returned HTML instead of an image.\n\n' +
                    'To test image sharing locally, run:\n' +
                    '  netlify dev\n\n' +
                    'Or test on the deployed site where functions are available.');
            } else {
              alert('⚠️ Unable to generate share image.\n\n' +
                    'The image generation service returned an error page.\n' +
                    'Please try again later or contact support.');
            }
            return;
          }
          
          // Verify it's actually an image
          if (!contentType.includes('image/') && !blob.type.includes('image/')) {
            const blobType = blob.type;
            console.error('Unexpected content type:', contentType, blobType);
            alert('⚠️ Unexpected response format. Expected image, got: ' + (contentType || blobType || 'unknown'));
            return;
          }
          
          // Ensure blob type is correct
          const finalBlob = blob.type === 'image/png' ? blob : new Blob([blob], { type: 'image/png' });
          const imageFile = new File([finalBlob], fileName, { 
            type: blob.type || 'image/png',
            lastModified: Date.now()
          });
          
          const shareDataWithFile: ShareData = {
            title: shareTitle,
            text: shareText,
            files: [imageFile],
          };
          
          // Try to share files - don't rely too heavily on canShare as it's unreliable
          const nav = navigator as any;
          // Check canShare if available, but try sharing anyway if it's not available
          if (!nav.canShare || nav.canShare(shareDataWithFile)) {
            await navigator.share(shareDataWithFile);
            shared = true;
          }
        } catch (fileErr: any) {
          // If file-sharing fails, we silently fall back to URL sharing
          if (fileErr.name === 'AbortError') {
            // User cancelled, don't try fallback
            return;
          }
          console.warn('File share failed, falling back to URL share:', fileErr);
        }
      }
      
      // Fallback: share a simple URL (works on most mobile browsers)
      if (!shared && navigator.share) {
        const shareData: ShareData = {
          title: shareTitle,
          text: shareText,
          url: shareUrl,
        };
        
        await navigator.share(shareData);
      } else if (!shared) {
        // If we get here and navigator.share doesn't exist, show error
        throw new Error('navigator.share is not available');
      }
    } catch (err: any) {
      console.error('Share failed:', err);
      console.error('Error details:', {
        name: err?.name,
        message: err?.message,
        stack: err?.stack,
        navigatorShareExists: typeof navigator !== 'undefined' ? !!navigator.share : false,
        navigatorShareType: typeof navigator !== 'undefined' ? typeof navigator.share : 'navigator undefined'
      });
      
      // If user cancelled, don't show error
      if (err?.name === 'AbortError') {
        return;
      }
      
      // Only show "not supported" if we're absolutely sure navigator.share doesn't exist
      // Check for specific error messages that indicate sharing isn't supported
      const isNotSupportedError = 
        (err?.name === 'TypeError' && err?.message?.includes('share') && err?.message?.includes('not a function')) ||
        (err?.name === 'TypeError' && err?.message?.includes('Cannot read') && err?.message?.includes('share'));
      
      if (isNotSupportedError && typeof navigator !== 'undefined' && !navigator.share) {
        alert('Sharing is not supported on this device/browser.');
        return;
      }
      
      // Show user-friendly error message with debug info in console
      alert('Unable to share image right now. Please check the console for details.');
    }
  }, [currentCase, buildShareImageUrl, buildShareText, includeShareUrl, shareCardUrl]);

  const renderShareCard = ({ showButton = false, onShareClick }: { showButton?: boolean; onShareClick?: () => void } = {}) => {
    if (!currentCase) return null;
    const resolvedShareHandler =
      onShareClick ||
      (() => {
        setShowShareSheet(true);
      });

    const shareImageUrl = buildShareImageUrl();

    return (
      <div className="bg-transparent">
        <div className="relative">
          {/* Display the generated share card image */}
          <img
            src={shareImageUrl}
            alt="Case solved share card"
            className="w-full rounded-2xl border-2 border-purple-300 shadow-2xl"
            onError={(e) => {
              console.error('Share card image failed to load:', shareImageUrl);
              // Hide the broken image
              e.currentTarget.style.display = 'none';
            }}
          />
          {showButton && (
            <div className="mt-4 flex justify-center">
              <button
                onClick={resolvedShareHandler}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-purple-600 hover:bg-purple-700 text-white font-oswald text-sm uppercase tracking-wide shadow-lg transition-colors"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.13-4.16c.53.5 1.24.81 2.03.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.08 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z" />
                </svg>
                Share Result
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Helper to format date with day of the week
  function formatDateWithDay(dateString: string): string {
    let date: Date;
    
    // Handle ISO format dates like "20250804"
    if (/^\d{8}$/.test(dateString)) {
      const year = parseInt(dateString.substring(0, 4));
      const month = parseInt(dateString.substring(4, 6)) - 1; // Month is 0-indexed
      const day = parseInt(dateString.substring(6, 8));
      date = new Date(year, month, day);
    } else {
      // Handle standard date formats
      date = new Date(dateString);
    }
    
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    const day = days[date.getDay()];
    const dateNum = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    
    // Add ordinal suffix
    const suffix = dateNum === 1 || dateNum === 21 || dateNum === 31 ? 'st' :
                   dateNum === 2 || dateNum === 22 ? 'nd' :
                   dateNum === 3 || dateNum === 23 ? 'rd' : 'th';
    
    return `${day} ${dateNum}${suffix} ${month} ${year}`;
  }

  // Helper function to find the maximum numbered image available (v1.jpg, v2.jpg, etc.)
  function getMaxNumberedImage(): number {
    // Try images 1 through 31 to find the maximum available
    for (let i = 31; i >= 1; i--) {
      try {
        require(`./small-case-images/v${i}.jpg`);
        return i;
      } catch {
        continue;
      }
    }
    return 1; // Default to 1 if no images found
  }

  function getCaseImageSrc(): string | null {
    if (!currentCase) return null;
    
    // Get case date's day of month
    const caseDate = currentCase.incident?.date || currentCase.date;
    if (!caseDate) return null;
    
    let date: Date;
    // Handle ISO format dates like "20250804"
    if (caseDate.length === 8 && /^\d{8}$/.test(caseDate)) {
      const year = parseInt(caseDate.substring(0, 4));
      const month = parseInt(caseDate.substring(4, 6)) - 1;
      const day = parseInt(caseDate.substring(6, 8));
      date = new Date(year, month, day);
    } else {
      date = new Date(caseDate);
    }
    
    const dayOfMonth = date.getDate(); // 1-31
    
    // Find maximum available numbered image
    const maxImageNumber = getMaxNumberedImage();
    
    // Calculate which image to use: dayOfMonth % maxImageNumber
    // If result is 0, use maxImageNumber instead
    let imageNumber = dayOfMonth % maxImageNumber;
    if (imageNumber === 0) {
      imageNumber = maxImageNumber;
    }
    
    // Try to load the calculated image, fallback to v1.jpg if it fails
    try {
      return require(`./small-case-images/v${imageNumber}.jpg`);
    } catch {
      try {
        return require(`./small-case-images/v1.jpg`);
    } catch {
      return null;
      }
    }
  }


  // Normalize mis-encoded punctuation (mojibake) commonly seen when UTF-8 is read as Windows-1252
  function decodeMojibake(text: string | undefined | null): string {
    if (!text) return '';
    return String(text)
      // Curly quotes and apostrophes
      .replace(/â/g, '’')
      .replace(/â/g, '‘')
      .replace(/â/g, '“')
      .replace(/â/g, '”')
      // Dashes and ellipsis
      .replace(/â/g, '—')
      .replace(/â/g, '–')
      .replace(/â¦/g, '…')
      // Misc stray Â characters
      .replace(/Â /g, ' ')
      .replace(/Â/g, '')
      // Common accented letters that sometimes appear mis-encoded
      .replace(/Ã©/g, 'é')
      .replace(/Ã¨/g, 'è')
      .replace(/Ãª/g, 'ê')
      .replace(/Ã«/g, 'ë')
      .replace(/Ã¡/g, 'á')
      .replace(/Ã /g, 'à')
      .replace(/Ã¢/g, 'â')
      .replace(/Ã£/g, 'ã')
      .replace(/Ã¼/g, 'ü')
      .replace(/Ã¶/g, 'ö')
      .replace(/Ã±/g, 'ñ')
      // Replace em dashes (—, U+2014) and en dashes (–, U+2013) with regular hyphens (-) for consistent display
      .replace(/\u2014/g, '-')  // Em dash
      .replace(/\u2013/g, '-')  // En dash
      .replace(/—/g, '-')       // Em dash (fallback)
      .replace(/–/g, '-');       // En dash (fallback)
  }

  // Very small formatter: supports *italic* using <em>. Escapes other HTML.
  function formatInline(text: string) {
    // Escape HTML entities first
    const escaped = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    // Convert *italic* runs to <em>…</em>
    const withItalics = escaped.replace(/\*(.*?)\*/g, '<em>$1</em>');

    // Render as HTML (safe because we escaped first and only allow <em>)
    return <span dangerouslySetInnerHTML={{ __html: withItalics }} />;
  }





  // Note: evaluateVerifier function removed as it was unused

  // Function to handle revealing a clue
  const handleRevealClue = (clueIndex: number) => {
    if (gameEnded || revealedClues.has(clueIndex)) return;
    
    // Only allow revealing clues in sequential order (0, 1, 2, 3...)
    const nextExpectedClue = revealedClues.size;
    if (clueIndex !== nextExpectedClue) {
      console.log(`Cannot reveal clue ${clueIndex + 1}. Must reveal clue ${nextExpectedClue + 1} first.`);
      return;
    }
    
    setRevealedClues(prev => new Set(prev).add(clueIndex));
    setTurnCount(prev => prev + 1);
    
    // Track when this clue was revealed to prevent accidental cross-out
    setClueRevealTimes(prev => new Map(prev).set(clueIndex, Date.now()));
    
    // Advance story progress: if this is the final clue, reveal the rest of the story
    setStoryProgress(prev => {
      if (!currentCase) return prev;
      const totalClues = (currentCase.verifiers?.length) || 0;
      const revealedCount = revealedClues.size; // before adding this one
      const isFinalClue = revealedCount + 1 >= totalClues;
      return isFinalClue
        ? Math.max(0, storySentences.length - 1) // reveal all remaining sentences
        : Math.min(prev + 1, storySentences.length - 1);
    });
    
    // Track clue reveal
    if (currentCase) {
      analytics.trackClueReveal(clueIndex + 1, currentCase.incident?.date || 'unknown', currentCase.case_id);
    }
    
    // No longer ending game by 8-turn rule
  };

  // Note: handleRevealFieldMemo function removed as it was unused

  // Refactor handleSubmitProposalWithTurn to accept an optional proposal argument
  const handleSubmitProposalWithTurn = (proposalOverride?: { being: string; device: string; hideout: string }) => {
    const proposalToSubmit = proposalOverride || currentProposal;
    if (!currentCase || !proposalToSubmit.being || !proposalToSubmit.device || !proposalToSubmit.hideout) {
      return;
    }
    if (gameEnded) return;

    const results = checkProposal(proposalToSubmit, currentCase);
    const newProposalResult = { proposal: proposalToSubmit, results };

    setProposals([...proposals, newProposalResult]);
    
    // Check if this should be a free proposal
    const canUseFreeProposal = !freeProposalUsed && revealedClues.size <= 2;
    if (canUseFreeProposal) {
      setFreeProposalUsed(true);
      // Don't increment turn count for free proposal
    } else {
      setTurnCount(prev => prev + 1);
    }

    // Track proposal submission
    if (currentCase) {
      const correctCount = results.filter(r => r.result).length;
      analytics.trackProposalSubmission(
        isCorrectSolution(proposalToSubmit, currentCase),
        correctCount,
        currentCase.incident?.date || 'unknown',
        currentCase.case_id
      );
    }

    // Check if this is the correct solution
    if (isCorrectSolution(proposalToSubmit, currentCase)) {
      setGameWon(true);
      setGameEnded(true);
      console.debug('Game won!');
      
      // Record stats based on game mode
      if (currentCase) {
        // Only pass caseDate for archive cases (not today's case)
        const today = new Date().toISOString().split('T')[0];
        const isArchiveCase = currentCase.incident.date !== today;
        const caseDate = isArchiveCase ? currentCase.incident.date : undefined;
        
        console.log('Archive completion debug:', {
          currentCaseDate: currentCase.incident.date,
          today,
          isArchiveCase,
          caseDate
        });
        
        if (newGameMode) {
          // Single proposal mode: record success
          statsService.recordSingleProposalResult(true, caseDate);
          
          // Track case solved for new game mode
          analytics.trackCaseSolved(
            1, // Single move
            1, // PAR = 1 for single proposal mode
            currentCase.incident?.date || 'unknown',
            currentCase.case_id,
            'single_proposal'
          );
        } else {
          // Standard mode: record with par score
          const finalTurnCount = turnCount + (canUseFreeProposal ? 0 : 1);
          const parScore = finalTurnCount - (currentCase.verifiers.length + 1); // PAR = clues + 1
        statsService.recordCaseSolved(parScore, caseDate);
        
        // Track game completion
        analytics.trackGameCompletion(
          finalTurnCount,
          currentCase.verifiers.length + 1, // PAR = clues + 1
          currentCase.incident?.date || 'unknown',
          currentCase.case_id
        );
        
        // Track case solved
        analytics.trackCaseSolved(
          finalTurnCount,
          currentCase.verifiers.length + 1, // PAR = clues + 1
          currentCase.incident?.date || 'unknown',
          currentCase.case_id,
          'standard'
        );
        }
      }
    } else {
      // Reveal all remaining clues on failed proposal
      if (currentCase) {
        const totalClues = currentCase.verifiers?.length || 0;
        const allClueIndices = Array.from({ length: totalClues }, (_, i) => i);
        setRevealedClues(new Set(allClueIndices));
      }
      
      if (newGameMode) {
        // New mode: game ends immediately on wrong proposal
        setGameEnded(true);
        setShowSolution(true);
        console.debug('Game lost: wrong proposal in single-attempt mode');
        
        // Record failed attempt in single proposal mode
        if (currentCase) {
          const today = new Date().toISOString().split('T')[0];
          const isArchiveCase = currentCase.incident.date !== today;
          const caseDate = isArchiveCase ? currentCase.incident.date : undefined;
          statsService.recordSingleProposalResult(false, caseDate);
          
          // Track case failed
          analytics.trackCaseFailed(
            currentCase.incident?.date || 'unknown',
            currentCase.case_id,
            'single_proposal'
          );
        }
      } else {
        // Standard mode: increment failed proposals counter only when not a free proposal
      if (!canUseFreeProposal) {
        setFailedProposals(prev => {
          const next = prev + 1;
          if (next >= 3) {
      setGameEnded(true);
            console.debug('Game lost: 3 unsuccessful proposals');
            
            // Track case failed for standard mode
            if (currentCase) {
              analytics.trackCaseFailed(
                currentCase.incident?.date || 'unknown',
                currentCase.case_id,
                'standard'
              );
            }
          }
          return next;
        });
        }
      }
    }
    // Clear currentProposal if this was a checklist submission
    if (proposalOverride) {
      setCurrentProposal({ being: '', device: '', hideout: '' });
    }
    
    // Scroll to show the proposal result after a short delay to ensure it's rendered
    setTimeout(() => {
      proposalsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };



  const toggleCrossOffBeing = (name: string) => {
    setCrossedOffBeings(prev => prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]);
  };

  const toggleCrossOffDevice = (name: string) => {
    setCrossedOffDevices(prev => prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]);
  };

  const toggleCrossOffHideout = (name: string) => {
    setCrossedOffHideouts(prev => prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]);
  };

  const toggleCrossOffClue = (clueIndex: number) => {
    // Check if clue was recently revealed (within 1 second)
    const revealTime = clueRevealTimes.get(clueIndex);
    if (revealTime && Date.now() - revealTime < 1000) {
      // Too soon after reveal - ignore click
      return;
    }
    
    setCrossedOffClues(prev => {
      const newSet = new Set(prev);
      if (newSet.has(clueIndex)) {
        newSet.delete(clueIndex);
      } else {
        newSet.add(clueIndex);
      }
      return newSet;
    });
  };


  // Hints data - loaded from hints_week46.json, hints_week47.json, hints_week48.json, and hints_week49.json
  const [hintsDataCache, setHintsDataCache] = useState<Record<string, any> | null>(null);
  
  useEffect(() => {
    // Load hints data once from both week 46 and week 47
    if (!hintsDataCache) {
      const loadHints = async () => {
        const allHints: Record<string, any> = {};
        
        // Load week 46 hints
        try {
          const week46Module = await import('./hints_week46.json');
          Object.assign(allHints, week46Module.default);
        } catch (e) {
          console.log('Week 46 hints not available');
        }
        
        // Load week 47 hints
        try {
          const week47Module = await import('./hints_week47.json');
          Object.assign(allHints, week47Module.default);
        } catch (e) {
          console.log('Week 47 hints not available');
        }
        
        // Load week 48 hints
        try {
          const week48Module = await import('./hints_week48.json');
          Object.assign(allHints, week48Module.default);
        } catch (e) {
          console.log('Week 48 hints not available');
        }
        
        // Load week 49 hints
        try {
          const week49Module = await import('./hints_week49.json');
          Object.assign(allHints, week49Module.default);
        } catch (e) {
          console.log('Week 49 hints not available');
        }
        
        setHintsDataCache(allHints);
      };
      
      loadHints();
    }
  }, [hintsDataCache]);
  
  const getHintsForCase = () => {
    if (!currentCase?.case_id || !hintsDataCache) return null;
    
    // Check if we have hints for this case
    const caseHints = hintsDataCache[currentCase.case_id];
    if (caseHints && caseHints.hints && caseHints.hints.length > 0) {
      return caseHints;
    }
    
    return null;
  };

  const renderBetaLayout = () => {
    if (!currentCase) return null;
    const difficulty = calculateDifficulty(currentCase.verifiers);
    const hintsData = getHintsForCase();
    const fluxRating =
      currentCase.incident?.Stars !== undefined
        ? renderStars(currentCase.incident.Stars).replace(/★/g, '⚡')
        : '—';
    const affinityEmoji = currentCase.incident?.Affinity
      ? getAffinityEmoji(currentCase.incident.Affinity)
      : '';

    // Reorder clues so the longest is last
    const reorderedClueOrder = [...clueOrder].sort((a, b) => {
      const descA = currentCase.verifiers?.[a]?.Description?.length || 0;
      const descB = currentCase.verifiers?.[b]?.Description?.length || 0;
      // Put longest at the end
      return descA - descB;
    });

    // Helper function to bold key phrases at the start of clues
    const formatClueText = (text: string) => {
      // Bold common prefixes like "FALSE:", "TRUE:", etc.
      const boldPrefixes = ['FALSE:', 'TRUE:', 'NOT:', 'EXACTLY', 'AT LEAST', 'AT MOST'];
      for (const prefix of boldPrefixes) {
        if (text.toUpperCase().startsWith(prefix)) {
          const prefixLength = prefix.length;
          const prefixText = text.substring(0, prefixLength);
          const restText = text.substring(prefixLength);
          return (
        <>
              <span className="font-bold">{prefixText}</span>
              {restText}
            </>
          );
        }
      }
      // If no prefix match, bold the first phrase (up to first comma or period, or first 5 words)
      const firstPhraseMatch = text.match(/^([^,.]{1,40}|[\w\s]{1,30})/);
      if (firstPhraseMatch) {
        const firstPhrase = firstPhraseMatch[1].trim();
        const restText = text.substring(firstPhrase.length);
        return (
          <>
            <span className="font-bold">{firstPhrase}</span>
            {restText}
          </>
        );
      }
      return text;
    };

    const renderBetaSuspectColumn = (
      title: string,
      items: (Being | Device | Hideout)[],
      crossed: string[],
      selected: string[],
      toggle: (name: string) => void
    ) => (
      <div className="space-y-1">
        <div className="text-center font-oswald font-bold uppercase mb-1.5" style={{ fontSize: '11px', letterSpacing: '0.08em', color: '#25264A' }}>
          {title}
        </div>
        <div className="grid grid-cols-1 gap-1">
          {[...items]
            .sort((a, b) => a.Stars - b.Stars)
            .map((item) => {
              const isCrossed = crossed.includes(item.Name);
              const isSelected = selected.includes(item.Name);
              return (
                <button
                  key={item.Name}
                  className={`relative flex flex-col items-start justify-start transition-all duration-150 w-full ${
                    isSelected
                      ? `${getColorScheme().selected}`
                      : isCrossed
                        ? 'bg-gray-200'
                        : 'bg-white'
                  }`}
                  style={{ borderRadius: '12px', padding: '4px 8px', backgroundColor: isSelected ? undefined : isCrossed ? 'rgba(229, 231, 235, 0.9)' : 'rgba(255, 255, 255, 0.9)' }}
                  onClick={() => {
                    toggle(item.Name);
                    setActionJustTaken(false);
                  }}
                  type="button"
                >
                  <span
                    className={`font-oswald font-semibold w-full ${
                      isCrossed ? 'line-through opacity-60' : ''
                    } ${isSelected ? 'selected-grid-name' : ''}`}
                    style={{
                      fontSize: '13px',
                      color: '#141726',
                      marginBottom: '2px'
                    }}
                  >
                    {item.Name}
                  </span>
                  <div
                    className="flex items-center gap-1 w-full"
                    style={{
                      marginTop: '2px',
                      fontSize: '11px'
                    }}
                  >
                    <span>{getAffinityEmoji(item.Affinity)}</span>
                    <span className="text-yellow-600" style={{ fontSize: '11px' }}>
                      {renderStars(item.Stars)}
                    </span>
                  </div>
                </button>
              );
            })}
        </div>
      </div>
    );

    return (
      <div
        className={`min-h-screen text-white ${
          colorScheme === 0
            ? 'bg-gradient-to-br from-slate-800 via-slate-700 to-slate-600'
            : colorScheme === 1
              ? 'bg-gradient-to-br from-gray-900 via-purple-900 to-blue-900'
              : 'bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800'
        }`}
      >
        <TopNavigation user={user} onSignInClick={() => {}} onSignOut={() => {}} />
        <div className="pt-1 px-1 pb-2 sm:pt-1 sm:px-2 sm:pb-3">
          <div className="max-w-xl mx-auto w-full">
            <div className="bg-white bg-opacity-95 border-2 border-gray-200 rounded-3xl shadow-2xl p-2 sm:p-3 space-y-2" style={{ overflow: 'visible' }}>
              <div className="flex flex-col gap-1">
                <div className="flex flex-wrap items-start justify-between gap-1">
                  <div>
                    <p className="text-[11px] font-oswald uppercase tracking-[0.35em] text-gray-500">
                      Mobile Beta
                    </p>
                    <h1 className="text-2xl sm:text-3xl font-oswald font-bold text-gray-900 leading-tight">
                      {decodeMojibake(getHeadline(currentCase))}
                    </h1>
                  </div>
                  <div className="flex items-center gap-2">
                    <Link
                      to="/"
                      className="text-xs sm:text-sm font-oswald text-purple-700 underline decoration-dotted hover:decoration-solid"
                    >
                      Back to classic view
                    </Link>
                  </div>
                </div>
                <p className="text-[13px] sm:text-base text-gray-800 font-lora leading-relaxed text-justify">
                  {formatInline(decodeMojibake(getDescription(currentCase)))}
                </p>
              </div>

              {/* Investigation Notebook Section */}
              <div className="mb-0.5">
                <div className="text-center">
                  <div className="text-sm sm:text-base font-oswald mb-1 px-3 py-1 rounded uppercase" style={{ fontFamily: "'Oswald', sans-serif", backgroundColor: '#000000', color: '#E8D5B7' }}>
                    <span style={{ fontSize: '0.85em' }}>⚡</span> SOLVE TODAY'S MYSTERY BEFORE REALITY COLLAPSES <span style={{ fontSize: '0.85em' }}>⚡</span>
                  </div>
                </div>
              </div>


              <section className="space-y-1">
                <div className="mb-1" style={{ marginTop: '0px', marginBottom: '6px' }}>
                  <h2 className="text-base font-oswald font-bold uppercase tracking-[0.1em]" style={{ color: '#25264A' }}>
                    INCIDENT - {difficulty} - {currentCase.incident?.date ? formatDateWithDay(currentCase.incident.date) : '—'}
                  </h2>
                  <div className="mt-1" style={{ height: '1px', backgroundColor: '#E1E4FF' }}></div>
                </div>
                <div className="grid grid-cols-1 gap-1 text-xs">
                  <button
                    type="button"
                    className="relative flex items-center justify-start transition-all duration-150 bg-white"
                    style={{ borderRadius: '10px', padding: '4px 8px', marginBottom: '2px' }}
                  >
                    <p className="text-xs leading-tight font-lora font-normal text-left whitespace-nowrap overflow-hidden text-ellipsis" style={{ color: '#141726', lineHeight: '1.2' }}>
                      <span>Location: {currentCase.incident?.location || '—'}</span>
                    </p>
                  </button>
                  <button
                    type="button"
                    className="relative flex items-center justify-start transition-all duration-150 bg-white"
                    style={{ borderRadius: '10px', padding: '4px 8px', marginBottom: '2px' }}
                  >
                    <p className="text-xs leading-tight font-lora font-normal text-left whitespace-nowrap" style={{ color: '#141726', lineHeight: '1.2' }}>
                      <span>Flux: {fluxRating}</span>
                      {' • '}
                      <span>Affinity: {affinityEmoji}</span>
                    </p>
                  </button>
                </div>
              </section>

              <section className="space-y-1">
                <div className="mb-1 flex items-center justify-between" style={{ marginTop: '4px', marginBottom: '6px' }}>
                  <h2 className="text-base font-oswald font-bold uppercase tracking-[0.1em]" style={{ color: '#25264A' }}>
                    Clues
                  </h2>
                  <span className="text-[10px] font-oswald uppercase tracking-wide" style={{ color: '#25264A', opacity: 0.6 }}>
                    Tap to cross off
                  </span>
                </div>
                <div className="mb-1" style={{ height: '1px', backgroundColor: '#E1E4FF' }}></div>
                <div className="grid grid-cols-1 gap-1 text-xs">
                  {reorderedClueOrder.length > 0 &&
                    reorderedClueOrder.map((actualIdx, displayIdx) => {
                      if (
                        actualIdx === undefined ||
                        actualIdx < 0 ||
                        actualIdx >= currentCase.verifiers.length
                      ) {
                        return null;
                      }
                      const verifier = currentCase.verifiers[actualIdx];
                      const isCrossed = crossedOffClues.has(actualIdx);
                      return (
                        <button
                          key={displayIdx}
                          onClick={() => toggleCrossOffClue(actualIdx)}
                          type="button"
                          className={`relative flex flex-row items-center justify-start text-left transition-all duration-150 ${
                            isCrossed
                              ? 'bg-gray-200'
                              : 'bg-white'
                          }`}
                          style={{ borderRadius: '10px', padding: '6px 10px', marginBottom: '4px' }}
                        >
                          <span className="inline-flex items-center justify-center rounded-full bg-gray-800 text-white font-oswald font-bold flex-shrink-0 mr-1.5" style={{ width: '18px', height: '18px', fontSize: '11px' }}>
                            {displayIdx + 1}
                          </span>
                          <p className={`text-xs font-lora font-normal flex-1 ${
                            isCrossed ? 'line-through opacity-60' : ''
                          }`} style={{ color: '#141726', lineHeight: '1.2' }}>
                            {formatClueText(verifier.Description)}
                          </p>
                        </button>
                      );
                    })}
                </div>
                {/* Hints Display Section */}
                {showHints && hintsData && (
                  <div className="mt-2 mb-2 bg-white rounded-lg p-3 border border-gray-300 relative">
                    <button
                      onClick={() => setShowHints(false)}
                      className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 transition-colors"
                      title="Close hints"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </button>
                    <div className="space-y-2 max-h-96 overflow-y-auto mb-2">
                      <div className="py-1">
                        <div className="text-xs font-oswald font-bold mb-1 text-left" style={{ color: '#25264A' }}>
                          HINT {currentHintLevel + 1}
                        </div>
                        <div className="text-xs text-gray-700 mt-1 whitespace-pre-line font-lora leading-relaxed text-left">
                          {hintsData.hints[currentHintLevel]?.hint}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <button
                        onClick={() => setCurrentHintLevel(prev => Math.max(0, prev - 1))}
                        disabled={currentHintLevel === 0}
                        className={`text-xs font-oswald uppercase tracking-wide px-3 py-1 rounded transition-colors ${
                          currentHintLevel === 0
                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                        }`}
                      >
                        ← Previous
                      </button>
                      <span className="text-xs text-gray-600 font-oswald">
                        {currentHintLevel + 1} / {hintsData.hints.length}
                      </span>
                      <button
                        onClick={() => setCurrentHintLevel(prev => Math.min(hintsData.hints.length - 1, prev + 1))}
                        disabled={currentHintLevel === hintsData.hints.length - 1}
                        className={`text-xs font-oswald uppercase tracking-wide px-3 py-1 rounded transition-colors ${
                          currentHintLevel === hintsData.hints.length - 1
                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                        }`}
                      >
                        Next →
                      </button>
                    </div>
                  </div>
                )}
              </section>

              <section className="space-y-1">
                <div className="mb-1" style={{ height: '1px', backgroundColor: '#E1E4FF' }}></div>
                <div className="grid grid-cols-3 gap-1 text-xs relative">
                  <div className="absolute left-1/3 top-0 bottom-0 w-px" style={{ backgroundColor: 'rgba(255, 255, 255, 0.3)' }}></div>
                  <div className="absolute left-2/3 top-0 bottom-0 w-px" style={{ backgroundColor: 'rgba(255, 255, 255, 0.3)' }}></div>
                  {renderBetaSuspectColumn('Beings', currentCase.components?.beings || [], crossedOffBeings, selectedBeings, (name) => {
                    if (selectedBeings.includes(name)) {
                      setSelectedBeings(selectedBeings.filter((n) => n !== name));
                    } else {
                      setSelectedBeings([...selectedBeings, name]);
                    }
                  })}
                  {renderBetaSuspectColumn('Weapons', currentCase.components?.devices || [], crossedOffDevices, selectedDevices, (name) => {
                    if (selectedDevices.includes(name)) {
                      setSelectedDevices(selectedDevices.filter((n) => n !== name));
                    } else {
                      setSelectedDevices([...selectedDevices, name]);
                    }
                  })}
                  {renderBetaSuspectColumn('Hideouts', currentCase.components?.hideouts || [], crossedOffHideouts, selectedHideouts, (name) => {
                    if (selectedHideouts.includes(name)) {
                      setSelectedHideouts(selectedHideouts.filter((n) => n !== name));
                    } else {
                      setSelectedHideouts([...selectedHideouts, name]);
                    }
                  })}
                </div>
              </section>

              <div className="space-y-1">
                <div className="flex flex-col gap-1">
                  <div className="flex flex-row gap-1 mt-1 justify-center items-center w-full">
                    <button
                      className={`w-1/3 min-h-[3rem] text-xs px-2 py-2 rounded-full font-semibold font-oswald transition-colors duration-200 shadow-lg text-center
                        ${
                          gameWon ||
                          gameEnded ||
                          actionJustTaken ||
                          selectedBeings.length + selectedDevices.length + selectedHideouts.length === 0
                            ? 'bg-gray-400 text-white cursor-not-allowed'
                            : `${getColorScheme().primary} text-white`
                        }
                      `}
                      disabled={
                        gameWon ||
                        gameEnded ||
                        actionJustTaken ||
                        selectedBeings.length + selectedDevices.length + selectedHideouts.length === 0
                      }
                      onClick={() => {
                        setCrossedOffBeings((prev) => {
                          let next = [...prev];
                          selectedBeings.forEach((name) => {
                            if (next.includes(name)) {
                              next = next.filter((n) => n !== name);
                            } else {
                              next.push(name);
                            }
                          });
                          return next;
                        });
                        setCrossedOffDevices((prev) => {
                          let next = [...prev];
                          selectedDevices.forEach((name) => {
                            if (next.includes(name)) {
                              next = next.filter((n) => n !== name);
                            } else {
                              next.push(name);
                            }
                          });
                          return next;
                        });
                        setCrossedOffHideouts((prev) => {
                          let next = [...prev];
                          selectedHideouts.forEach((name) => {
                            if (next.includes(name)) {
                              next = next.filter((n) => n !== name);
                            } else {
                              next.push(name);
                            }
                          });
                          return next;
                        });
                        setSelectedBeings([]);
                        setSelectedDevices([]);
                        setSelectedHideouts([]);
                        setActionJustTaken(true);
                      }}
                    >
                      (Un) Eliminate
                    </button>
                    <button
                      className={`w-1/3 min-h-[3rem] text-xs px-2 py-2 rounded-full font-semibold font-oswald transition-colors duration-200 shadow-lg text-center
                        ${
                          gameWon ||
                          gameEnded ||
                          failedProposals >= 3 ||
                          !(
                            selectedBeings.filter((b) => !crossedOffBeings.includes(b)).length === 1 &&
                            selectedDevices.filter((d) => !crossedOffDevices.includes(d)).length === 1 &&
                            selectedHideouts.filter((h) => !crossedOffHideouts.includes(h)).length === 1 &&
                            (newGameMode || revealedClues.size >= proposals.length + 1)
                          )
                            ? 'bg-gray-400 text-white cursor-not-allowed'
                            : `${getColorScheme().primary} text-white`
                        }
                      `}
                      disabled={
                        gameWon ||
                        gameEnded ||
                        failedProposals >= 3 ||
                        !(
                          selectedBeings.filter((b) => !crossedOffBeings.includes(b)).length === 1 &&
                          selectedDevices.filter((d) => !crossedOffDevices.includes(d)).length === 1 &&
                          selectedHideouts.filter((h) => !crossedOffHideouts.includes(h)).length === 1 &&
                          (newGameMode || revealedClues.size >= proposals.length + 1)
                        )
                      }
                      onClick={() => {
                        setConfirmationDialog({
                          show: true,
                          message: 'Are you sure you want to submit this solution?',
                          onConfirm: () => {
                            const being = selectedBeings.find((b) => !crossedOffBeings.includes(b));
                            const device = selectedDevices.find((d) => !crossedOffDevices.includes(d));
                            const hideout = selectedHideouts.find((h) => !crossedOffHideouts.includes(h));
                            if (being && device && hideout) {
                              handleSubmitProposalWithTurn({ being, device, hideout });
                              setSelectedBeings([]);
                              setSelectedDevices([]);
                              setSelectedHideouts([]);
                              setActionJustTaken(true);
                            }
                            setConfirmationDialog({ show: false, message: '', onConfirm: () => {} });
                          }
                        });
                      }}
                    >
                      Submit Solution
                    </button>
                    <button
                      className={`w-1/3 min-h-[3rem] text-xs px-2 py-2 rounded-full font-semibold font-oswald transition-colors duration-200 shadow-lg text-center ${getColorScheme().primary} text-white`}
                      onClick={() => {
                        setConfirmationDialog({
                          show: true,
                          message: 'Are you sure you want to clear all eliminations and retry? This will reset your progress.',
                          onConfirm: () => {
                            setCrossedOffBeings([]);
                            setCrossedOffDevices([]);
                            setCrossedOffHideouts([]);
                            setGameWon(false);
                            setGameEnded(false);
                            setShowSolution(false);
                            setSelectedBeings([]);
                            setSelectedDevices([]);
                            setSelectedHideouts([]);
                            setActionJustTaken(false);
                            setConfirmationDialog({ show: false, message: '', onConfirm: () => {} });
                          }
                        });
                      }}
                    >
                      Clear / Retry
                    </button>
                  </div>
                </div>
                <div className="text-xs text-gray-600 text-center">
                  {newGameMode
                    ? 'Select a being, weapon and hideout to submit your single proposal.'
                    : `Reveal at least ${proposals.length + 1} clue${
                        proposals.length + 1 === 1 ? '' : 's'
                      } before each proposal.`}
                </div>
              </div>

              {/* Proposals Section */}
              {proposals.length > 0 && (
                <section className="space-y-3 mt-6">
                  <h2 className="text-lg font-oswald uppercase tracking-wide text-gray-800">
                    Previous Proposals
                  </h2>
                  <div className="space-y-3">
                    {[...proposals].reverse().map((proposalResult, pIdx) => {
                      const beingObj = currentCase.components?.beings?.find(b => b.Name === proposalResult.proposal.being);
                      const deviceObj = currentCase.components?.devices?.find(d => d.Name === proposalResult.proposal.device);
                      const hideoutObj = currentCase.components?.hideouts?.find(h => h.Name === proposalResult.proposal.hideout);
                      const allPass = proposalResult.results.every(r => r.result);
                      return (
                        <div key={pIdx} className={`rounded-2xl border p-4 ${getColorScheme().panel}`}>
                          <div className="text-xs font-oswald uppercase tracking-wide text-gray-500 mb-2">
                            Proposal {proposals.length - pIdx}
                            {allPass ? (
                              <span className="ml-2 px-2 py-0.5 rounded-full bg-green-100 text-green-800 text-[10px] font-bold">
                                ✓ Correct
                              </span>
                            ) : (
                              <span className="ml-2 px-2 py-0.5 rounded-full bg-red-100 text-red-800 text-[10px] font-bold">
                                ✗ Incorrect
                              </span>
                            )}
                          </div>
                          <div className="grid grid-cols-3 gap-2 text-xs">
                            <div>
                              <div className="text-[10px] font-oswald uppercase text-gray-500 mb-1">Being</div>
                              <div className={`px-2 py-1 rounded-lg border ${
                                proposalResult.proposal.being === currentCase.solution.being
                                  ? 'bg-green-100 border-green-300 text-green-800'
                                  : 'bg-red-100 border-red-300 text-red-800'
                              }`}>
                                {beingObj?.Name || proposalResult.proposal.being}
                              </div>
                            </div>
                            <div>
                              <div className="text-[10px] font-oswald uppercase text-gray-500 mb-1">Weapon</div>
                              <div className={`px-2 py-1 rounded-lg border ${
                                proposalResult.proposal.device === currentCase.solution.device
                                  ? 'bg-green-100 border-green-300 text-green-800'
                                  : 'bg-red-100 border-red-300 text-red-800'
                              }`}>
                                {deviceObj?.Name || proposalResult.proposal.device}
                              </div>
                            </div>
                            <div>
                              <div className="text-[10px] font-oswald uppercase text-gray-500 mb-1">Hideout</div>
                              <div className={`px-2 py-1 rounded-lg border ${
                                proposalResult.proposal.hideout === currentCase.solution.hideout
                                  ? 'bg-green-100 border-green-300 text-green-800'
                                  : 'bg-red-100 border-red-300 text-red-800'
                              }`}>
                                {hideoutObj?.Name || proposalResult.proposal.hideout}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              )}

              {/* Solution Display */}
              {(gameWon || showSolution) && (
                <section className="mt-6 space-y-3" ref={caseSolvedRef}>
                  <h2 className="text-lg font-oswald uppercase tracking-wide text-gray-800">
                    Case Solved
                  </h2>
                  {gameWon && (
                    <div className="space-y-3">
                      {SHARE_PANEL_UPDATES_ENABLED &&
                        renderShareCard({
                          showButton: true,
                          onShareClick: () => setShowShareSheet(true)
                        })}
                    </div>
                  )}
                  <div className="bg-green-50 border-2 border-green-300 rounded-2xl p-4 space-y-2">
                    <div className="text-sm font-oswald font-bold text-green-800 mb-2">
                      ✓ Correct Solution
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                      <div>
                        <div className="text-[10px] font-oswald uppercase text-gray-500 mb-1">Being</div>
                        <div className="px-2 py-1 rounded-lg bg-white border border-gray-200">
                          {currentCase.solution.being}
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] font-oswald uppercase text-gray-500 mb-1">Weapon</div>
                        <div className="px-2 py-1 rounded-lg bg-white border border-gray-200">
                          {currentCase.solution.device}
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] font-oswald uppercase text-gray-500 mb-1">Hideout</div>
                        <div className="px-2 py-1 rounded-lg bg-white border border-gray-200">
                          {currentCase.solution.hideout}
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Navigation handlers for previous/next case
  const handlePreviousCase = () => {
    if (!currentCase) return;
    const idx = availableDates.findIndex(d => d === currentCase.incident.date);
    console.log('Previous: current date:', currentCase.incident.date, 'current idx:', idx, 'available dates:', availableDates);
    if (idx > 0) {
      // Clear saved game state when navigating
      GameStateService.clearGameState();
      startNewGameByDate(availableDates[idx - 1]);
    }
  };

  const handleNextCase = () => {
    if (!currentCase) return;
    const idx = availableDates.findIndex(d => d === currentCase.incident.date);
    console.log('Next: current date:', currentCase.incident.date, 'current idx:', idx, 'available dates:', availableDates);
    if (idx < availableDates.length - 1) {
      console.log('Navigating to:', availableDates[idx + 1]);
      // Clear saved game state when navigating
      GameStateService.clearGameState();
      startNewGameByDate(availableDates[idx + 1]);
    } else {
      console.log('At last date, cannot go further');
    }
  };

  // Note: handleTestCaseChange function removed as it was unused

  if (!currentCase) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        colorScheme === 0 ? 'bg-gradient-to-br from-slate-800 via-slate-700 to-slate-600' :
        colorScheme === 1 ? 'bg-gradient-to-br from-gray-900 via-purple-900 to-blue-900' :
        'bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800'
      }`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-purple-500 mx-auto"></div>
          <p className="mt-4 text-xl text-purple-300">Loading Bureau Gamma case files...</p>
          <p className="mt-2 text-sm text-purple-200">No cases available for today's date. Try selecting a different date.</p>
        </div>
      </div>
    );
  }

  // Confirmation Dialog Component
  const ConfirmationDialog = () => {
    if (!confirmationDialog.show) return null;

    return (
      <div 
        className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none"
      >
        <div 
          className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6 pointer-events-auto border-2 border-purple-300"
          onClick={(e) => e.stopPropagation()}
        >
          <h3 className="text-lg font-oswald font-bold text-gray-900 mb-4">
            Confirm Action
          </h3>
          <p className="text-sm text-gray-700 font-lora mb-6">
            {confirmationDialog.message}
          </p>
          <div className="flex gap-3 justify-end">
            <button
              onClick={() => setConfirmationDialog({ show: false, message: '', onConfirm: () => {} })}
              className="px-4 py-2 text-sm font-oswald uppercase tracking-wide bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                confirmationDialog.onConfirm();
              }}
              className="px-4 py-2 text-sm font-oswald uppercase tracking-wide bg-gray-800 text-white rounded hover:bg-gray-900 transition-colors"
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (isBetaLayout) {
    return (
      <>
        {renderBetaLayout()}
        <ConfirmationDialog />
      </>
    );
  }

  return (
    <>
      <ShareSheet />
    <div 
      className={`min-h-screen md:min-h-screen sm:min-h-0 text-white overflow-y-auto ${
        colorScheme === 0 ? 'bg-gradient-to-br from-slate-800 via-slate-700 to-slate-600' :
        colorScheme === 1 ? 'bg-gradient-to-br from-gray-900 via-purple-900 to-blue-900' :
        'bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800'
      }`}
      style={colorScheme === 0 ? { 
        background: 'linear-gradient(135deg, #1e293b 0%, #334155 50%, #475569 100%)' 
      } : {}}
    >
      {/* Top Navigation - All screen sizes */}
      <TopNavigation
        user={user}
        onSignInClick={() => {}}
        onSignOut={() => {}}
      />

      <div className="pt-1 px-2 pb-2 sm:pt-1 sm:px-4 sm:pb-4">
        {/* Header with logo */}
        <div className="flex justify-center mb-2">
          <div className="w-full md:max-w-xl">
            {/* Desktop: Logo with subtitle overlay */}
            <div className="relative">
              <img src={heroLogoSrc} alt="Dimension Flux" style={{ width: '100%', borderRadius: '1.5rem', boxShadow: '0 4px 32px 0 rgba(80, 0, 120, 0.10)', objectFit: 'contain', objectPosition: 'center' }} />
              {/* Get Started Button - Overlay bottom right */}
                <Link 
                  to="/getting-started" 
                  className="absolute bottom-2 right-2 sm:bottom-3 sm:right-3 bg-gray-800 hover:bg-gray-900 text-white px-2.5 sm:px-3 py-1.25 sm:py-1.75 rounded-full text-[10px] sm:text-xs font-oswald font-semibold uppercase tracking-wide transition-colors duration-200 shadow-lg"
                >
                  Get Started
                </Link>
            </div>
          </div>
        </div>

        {/* Clue Raider promo - under menu / under Dimension Flux banner */}
        <div className="mb-2">
          <ClueRaiderBanner />
        </div>

      {showNewCaseModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
          <div className="bg-gray-900 rounded-lg p-8 shadow-lg border border-purple-500 max-w-md w-full">
                            <h2 className="text-2xl font-oswald font-bold text-purple-200 mb-4">Start a New Game</h2>
            
            {/* Random Case Button */}
            <button
              className={`w-full mb-4 px-6 py-2 rounded-lg font-semibold font-oswald text-white shadow-lg transition-colors duration-200 text-lg ${getColorScheme().primary}`}
              onClick={startRandomGame}
            >
              Random Case
            </button>
            
            {/* Clear Saved Game Button - Admin only */}
            {isAdminMode && GameStateService.hasSavedGame() && (
              <button
                className="w-full mb-4 px-6 py-2 bg-red-600 hover:bg-red-700 rounded-lg font-semibold font-oswald text-white shadow-lg transition-colors duration-200 text-lg"
                onClick={() => {
                  GameStateService.clearGameState();
                  setShowNewCaseModal(false);
                  // Track clearing saved game
                  analytics.trackUserInteraction('saved_game_cleared');
                }}
              >
                Clear Saved Game
              </button>
            )}
            

            
            {/* Manual Date Input */}
            <div className="mb-4">
              <label htmlFor="dateInput" className="block text-purple-200 font-semibold mb-1">Enter Specific Date:</label>
              <input
                id="dateInput"
                type="date"
                value={dateInput}
                onChange={e => { setDateInput(e.target.value); setDateError(''); }}
                className="w-full px-3 py-2 rounded bg-gray-800 border border-purple-400 text-white focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
              <button
                className={`w-full mt-2 px-6 py-2 rounded-lg font-semibold font-oswald text-white shadow-lg transition-colors duration-200 text-lg ${getColorScheme().primary}`}
                onClick={() => {
                  console.log('Load Case by Date clicked, dateInput:', dateInput);
                  if (!dateInput) {
                    setDateError('Please select a date first.');
                    return;
                  }
                  startNewGameByDate(dateInput);
                }}
              >
                Load Case by Date
              </button>
              {dateError && (
                <div className="mt-2 text-red-400 text-sm text-center">
                  {dateError}
                </div>
              )}
            </div>
            
            <button
              className="w-full mt-4 px-6 py-2 bg-gray-700 hover:bg-gray-800 rounded-lg font-semibold font-oswald text-white shadow-lg transition-colors duration-200 text-lg"
              onClick={() => { 
                setShowNewCaseModal(false); 
                setDateInput(''); 
                setDateError('');
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {/* After the header image and 'New to' button: */}
      <div className="flex justify-center">
        <div className="w-full md:max-w-xl">
          {/* --- START main content block (lines 310-880) --- */}
          {/* Case File Heading Box - HIDDEN FOR NOW */}
          {/* 
          <div className="rounded-lg p-6 border-2 border-purple-300 shadow-lg shadow-purple-200/20 mb-6 w-full relative" style={{backgroundColor: 'white'}}>
            
            {/* Navigation buttons positioned at top corners - Admin only */}
            {/* 
            {isAdminMode && (
              <>
                <button
                  onClick={handlePreviousCase}
                  className={`absolute top-4 left-4 w-10 h-10 rounded-full font-semibold font-oswald text-white shadow-lg transition-colors duration-200 flex items-center justify-center text-lg z-10 ${getColorScheme().navigation}`}
                  aria-label="Previous Case"
                >
                  ←
                </button>
                <button
                  onClick={handleNextCase}
                  className={`absolute top-4 right-4 w-10 h-10 rounded-full font-semibold font-oswald text-white shadow-lg transition-colors duration-200 flex items-center justify-center text-lg z-10 ${getColorScheme().navigation}`}
                  aria-label="Next Case"
                >
                  →
                </button>
              </>
            )}
            
            <div className="text-center mb-2">
              {/* Main heading */}
              {/* 
              <h1 className="text-xl font-oswald font-bold text-[#2B2140] text-center leading-tight break-words mb-1" style={{
                fontFamily: "'Oswald', sans-serif",
                fontWeight: 700,
                letterSpacing: "0.10em",
                textTransform: "uppercase"
              }}>{isAdminMode ? 'BUREAU CASE FILE' : 'BUREAU GAMMA CASE FILE'}</h1>
              
              {/* Date */}
              {/* 
              <h1 className="text-base font-oswald font-bold text-[#2B2140] text-center leading-tight break-words mb-4" style={{
                fontFamily: "'Oswald', sans-serif",
                fontWeight: 700,
                letterSpacing: "0.10em",
                textTransform: "none"
              }}>
                {currentCase?.incident?.date ? formatDateWithDay(currentCase.incident.date) : 'Date Unknown'}
              </h1>
            </div>
            
            {currentCase.incident?.headline && (
              <div className="mb-4">
                {/* Grey line above headline */}
                {/* 
                <div className="w-full h-px bg-gray-400 mb-3"></div>
                <h2 className="text-2xl font-oswald font-bold text-[#2B2140] text-center leading-tight break-words">
                  {decodeMojibake(currentCase.incident.headline)}
                </h2>
                {/* Grey line below headline */}
                {/* 
                <div className="w-full h-px bg-gray-400 mt-3"></div>
              </div>
            )}
            
            <p className="text-[#1A1A1A] font-lora leading-relaxed mb-6 text-justify">
              {currentCase.incident?.description ?
                formatInline(decodeMojibake(currentCase.incident.description)) :
                'No case description available.'}
            </p>
            

          </div>
          */}




          {/* Move the checklist heading and subheading above the 'SOLVE THE CASE BY DISCOVERING:' section */}
          {/* In the checklist area (white box), add a centered heading and subheading above the grid */}
          <div className="w-full">
            {/* Inside the white card (case book section), at the very top: */}
            <div className={`flex flex-col items-center gap-2 rounded-xl px-6 py-4 md:px-6 md:py-5 shadow-lg w-full relative ${getColorScheme().panel}`} style={{ gap: '4px', overflow: 'visible' }}>
              
              {/* Navigation buttons positioned at top corners - Admin only */}
              {isAdminMode && (
                <>
                  <button
                    onClick={handlePreviousCase}
                    className={`absolute top-4 left-4 w-10 h-10 rounded-full font-semibold font-oswald text-white shadow-lg transition-colors duration-200 flex items-center justify-center text-lg z-10 ${getColorScheme().navigation}`}
                    aria-label="Previous Case"
                  >
                    ←
                  </button>
                  <button
                    onClick={handleNextCase}
                    className={`absolute top-4 right-4 w-10 h-10 rounded-full font-semibold font-oswald text-white shadow-lg transition-colors duration-200 flex items-center justify-center text-lg z-10 ${getColorScheme().navigation}`}
                    aria-label="Next Case"
                  >
                    →
                  </button>
                  
                  {/* Test cases disabled in production */}
                </>
              )}
              
              {/* Debug: Show generated share image above incident block */}
              {SHARE_DEBUG_PANEL_VISIBLE && (
              <div className="w-full mb-4 p-4 bg-gray-100 rounded-lg border-2 border-purple-300">
                <div className="text-xs font-oswald text-gray-600 mb-2 text-center font-bold">Generated Share Image (Debug):</div>
                {(() => {
                  const imageUrl = buildShareImageUrl();
                  console.log('Generated share image URL:', imageUrl);
                  
                  // Detect local development
                  const isLocalDev = typeof window !== 'undefined' && (
                    window.location.hostname === 'localhost' || 
                    window.location.hostname === '127.0.0.1' ||
                    /^192\.168\./.test(window.location.hostname) ||
                    /^10\./.test(window.location.hostname) ||
                    /^172\.(1[6-9]|2[0-9]|3[01])\./.test(window.location.hostname)
                  );
                  
                  return (
                    <>
                      {isLocalDev && (
                        <div className="mb-3 p-3 bg-yellow-100 border-2 border-yellow-400 rounded-lg">
                          <div className="text-xs font-bold text-yellow-900 mb-1">⚠️ LOCAL DEVELOPMENT DETECTED</div>
                          <div className="text-[10px] text-yellow-800">
                            Netlify functions don't work with <code className="bg-yellow-200 px-1 rounded">npm start</code>.
                            <br />
                            To test image sharing locally, run: <code className="bg-yellow-200 px-1 rounded">netlify dev</code>
                            <br />
                            Or test on the deployed site at <code className="bg-yellow-200 px-1 rounded">dimensionflux.com</code>
                          </div>
                        </div>
                      )}
                      <div className="text-[10px] font-mono text-gray-500 mb-2 break-all p-2 bg-white rounded border">
                        URL: {imageUrl}
                      </div>
                      <div className="text-[10px] font-mono text-gray-500 mb-2 p-2 bg-white rounded border max-h-40 overflow-auto">
                        Payload: {JSON.stringify({
                          date: shareCardDate,
                          difficulty: shareDifficultyValue,
                          headline: decodeMojibake(getHeadline(currentCase)),
                          solvedText: solvedDurationLabel,
                          imageUrl: (() => {
                            if (!caseImageSrc) return null;
                            if (caseImageSrc.startsWith('http://') || caseImageSrc.startsWith('https://')) return caseImageSrc;
                            if (caseImageSrc.startsWith('/')) return `${window.location.origin}${caseImageSrc}`;
                            try {
                              return new URL(caseImageSrc, window.location.origin).toString();
                            } catch {
                              return caseImageSrc;
                            }
                          })(),
                          logoUrl: (() => {
                            if (!heroLogoSrc) return null;
                            if (heroLogoSrc.startsWith('http://') || heroLogoSrc.startsWith('https://')) return heroLogoSrc;
                            if (heroLogoSrc.startsWith('/')) return `${window.location.origin}${heroLogoSrc}`;
                            try {
                              return new URL(heroLogoSrc, window.location.origin).toString();
                            } catch {
                              return heroLogoSrc;
                            }
                          })()
                        }, null, 2)}
                      </div>
                      <img
                        src={imageUrl}
                        alt="Generated share card"
                        className="w-full max-w-full mx-auto rounded-lg border-2 border-purple-300 shadow-lg"
                        style={{ height: 'auto', maxHeight: 'none' }}
                        onLoad={() => {
                          console.log('Share image loaded successfully');
                        }}
                        onError={(e) => {
                          console.error('Share image failed to load. URL:', imageUrl);
                          console.error('Error event:', e);
                          const errorDiv = document.createElement('div');
                          errorDiv.className = 'text-red-600 text-xs p-2 bg-red-50 rounded border border-red-300 mt-2';
                          errorDiv.textContent = `Failed to load image. Check console for details. URL: ${imageUrl.substring(0, 100)}...`;
                          e.currentTarget.parentElement?.appendChild(errorDiv);
                        }}
                      />
                      <button
                        onClick={async () => {
                          try {
                            console.log('Testing share image URL:', imageUrl);
                            const response = await fetch(imageUrl);
                            console.log('Response status:', response.status);
                            console.log('Response headers:', Object.fromEntries(response.headers.entries()));
                            const contentType = response.headers.get('content-type') || '';
                            const blob = await response.blob();
                            console.log('Response blob size:', blob.size, 'bytes');
                            console.log('Response blob type:', blob.type);
                            console.log('Content-Type header:', contentType);
                            
                            if (!response.ok) {
                              const text = await blob.text();
                              console.error('Response error text:', text);
                              alert(`Image fetch failed: ${response.status} ${response.statusText}\n\nResponse: ${text.substring(0, 200)}\n\nCheck console for details.`);
                            } else if (contentType.includes('text/html') || blob.type.includes('text/html')) {
                              const text = await blob.text();
                              console.error('Got HTML instead of image. Response:', text.substring(0, 500));
                              alert(`⚠️ Got HTML instead of image!\n\nThis usually means:\n1. Netlify function isn't running (use 'netlify dev' instead of 'npm start')\n2. Function returned an error page\n\nResponse preview:\n${text.substring(0, 300)}...\n\nCheck console for full response.`);
                            } else if (!contentType.includes('image/') && !blob.type.includes('image/')) {
                              const text = await blob.text();
                              console.error('Unexpected content type. Response:', text.substring(0, 500));
                              alert(`⚠️ Unexpected content type: ${contentType || blob.type}\n\nResponse preview:\n${text.substring(0, 300)}...\n\nCheck console for details.`);
                            } else {
                              alert(`✅ Image loaded successfully!\n\nSize: ${blob.size} bytes\nType: ${blob.type}\nContent-Type: ${contentType}`);
                            }
                          } catch (err) {
                            console.error('Fetch error:', err);
                            const errorMessage = err instanceof Error ? err.message : String(err);
                            alert(`❌ Fetch error: ${errorMessage}\n\nThis might mean:\n1. Netlify function isn't running\n2. CORS issue\n3. Network error\n\nCheck console for details.`);
                          }
                        }}
                        className="mt-2 px-4 py-2 bg-purple-600 text-white text-xs font-oswald uppercase rounded hover:bg-purple-700"
                      >
                        Test Image URL
                      </button>
                      <div className="mt-2 p-2 bg-yellow-50 border border-yellow-300 rounded text-xs text-yellow-800">
                        <strong>Note:</strong> Netlify functions only work when:
                        <ul className="list-disc list-inside mt-1 ml-2">
                          <li>Deployed to Netlify, OR</li>
                          <li>Running <code className="bg-yellow-100 px-1 rounded">netlify dev</code> (not <code className="bg-yellow-100 px-1 rounded">npm start</code>)</li>
                        </ul>
                        In development with <code className="bg-yellow-100 px-1 rounded">npm start</code>, the function URL will return a 404.
                      </div>
                    </>
                  );
                })()}
              </div>
              )}
              
              {/* Headline or Dossier Header */}
              {!showClassifiedView ? (
                <>
                  {/* v2: Display incident metadata with headline inside */}
                  {(() => {
                    const isV2 = currentCase.incident?.Stars !== undefined && currentCase.incident?.Affinity !== undefined;
                    if (!isV2) return null;
                    return (
                      <div className="bg-white bg-opacity-95 rounded-lg p-3 sm:p-4 border-2 border-gray-300 shadow-lg mb-1 w-full" style={{ marginTop: '-8px', marginBottom: '2px' }}>
                        {/* Dossier-like header */}
                        <div className="flex items-center justify-center text-xs sm:text-sm font-oswald font-bold text-gray-700 uppercase tracking-wide mb-1 sm:mb-1.5">
                          FLUX INCIDENT — DIFFICULTY:
                          <span
                            className={`ml-1 px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-bold ${
                              (() => {
                                const difficulty = calculateDifficulty(currentCase.verifiers);
                                return difficulty === 'EASY' ? 'bg-green-100 text-green-800' :
                                       difficulty === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800' :
                                       difficulty === 'HARD' ? 'bg-orange-100 text-orange-800' :
                                       'bg-red-100 text-red-800';
                              })()
                            }`}
                          >
                            {calculateDifficulty(currentCase.verifiers)}
                          </span>
                        </div>
                        <div className="w-full h-px bg-gray-200 mb-2 sm:mb-2.5"></div>
                        
                        {/* Headline inside incident box */}
                        <h2 className="w-full text-lg md:text-xl font-oswald font-bold text-[#2B2140] text-center leading-tight break-words md:leading-snug mb-2 sm:mb-2.5" style={{
                  letterSpacing: "0.10em"
                }}>
                  {decodeMojibake(getHeadline(currentCase))}
                </h2>
                        <div className="w-full h-px bg-gray-200 mb-1.5 sm:mb-2"></div>

                        {/* Dossier-like stacked fields - optimized for space */}
                        <div className="space-y-0.5 sm:space-y-1 leading-tight">
                          {/* DATE */}
                          <div className="flex items-center">
                            <div className="w-16 sm:w-20 flex-shrink-0">
                              <span className="text-xs sm:text-sm font-oswald font-bold text-gray-600 uppercase tracking-wide">DATE:</span>
                            </div>
                            <div className="flex-1 border-l border-gray-200 pl-2 sm:pl-3 flex items-center">
                              <span className="text-xs sm:text-sm text-gray-800 font-oswald whitespace-nowrap">
                                {formatDateWithDay(currentCase.incident.date)}
                              </span>
                            </div>
                          </div>

                          {/* LOCATION - can wrap to two lines */}
                          {currentCase.incident.location && (
                            <div className="flex items-start">
                              <div className="w-16 sm:w-20 flex-shrink-0">
                                <span className="text-xs sm:text-sm font-oswald font-bold text-gray-600 uppercase tracking-wide">LOCATION:</span>
                              </div>
                              <div className="flex-1 border-l border-gray-200 pl-2 sm:pl-3">
                                <span className="text-xs sm:text-sm text-gray-800 font-oswald break-words">{currentCase.incident.location}</span>
                              </div>
                            </div>
                          )}

                          {/* FLUX RATING & AFFINITY - combined on one line */}
                          {currentCase.incident.Stars !== undefined && currentCase.incident.Affinity && (
                            <div className="flex items-center">
                              <div className="w-16 sm:w-20 flex-shrink-0">
                                <span className="text-xs sm:text-sm font-oswald font-bold text-gray-600 uppercase tracking-wide">AFFINITY:</span>
                              </div>
                              <div className="flex-1 border-l border-gray-200 pl-2 sm:pl-3 flex items-center justify-between">
                                <span className="flex items-center gap-1 text-xs sm:text-sm">
                                  <span>{getAffinityEmoji(currentCase.incident.Affinity)}</span>
                                  <span className="font-oswald text-gray-900">{currentCase.incident.Affinity}</span>
                                </span>
                                <div className="flex items-center gap-0.5 sm:gap-1">
                                  <span className="text-xs sm:text-sm font-oswald font-bold text-gray-600 uppercase tracking-wide">FLUX RATING</span>
                                  <span className="text-gray-400">|</span>
                                  <span className="text-yellow-600 font-semibold text-xs sm:text-sm" style={{ letterSpacing: '-0.05em' }}>{renderStars(currentCase.incident.Stars).replace(/★/g, '⚡')}</span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </>
              ) : (
                <>
                  {/* Classified Dossier Header */}
                  <div className="bg-gray-100 w-full">
                    {/* Incident Block - Rounded Box */}
                    <div className="rounded-lg bg-white border border-gray-200 shadow-sm p-2" style={{ margin: 0, width: '100%', boxSizing: 'border-box' }}>
                    <div className="text-center">
                        <div className="text-sm sm:text-base font-oswald mb-1 px-3 py-1 rounded uppercase" style={{ fontFamily: "'Oswald', sans-serif", backgroundColor: '#000000', color: '#E8D5B7' }}>
                          FLUX REPORT {currentCase?.incident?.date ? formatDateWithDay(currentCase.incident.date).toUpperCase() : 'WED 10TH SEP 2025'}
                      </div>
                        <div className="text-lg sm:text-xl font-oswald font-bold text-gray-900 uppercase mb-2" style={{ letterSpacing: '0.10em' }}>
                          {decodeMojibake(getHeadline(currentCase))}
                    </div>
                        {/* Agent Report Section */}
                        <div className="text-left">
                          {(() => {
                            try {
                              // Get case date's day of month
                              if (!currentCase?.incident?.date) return null;
                              
                              const dateStr = currentCase.incident.date;
                              let date: Date;
                              
                              // Handle ISO format dates like "20250804"
                              if (dateStr.length === 8 && /^\d{8}$/.test(dateStr)) {
                                const year = parseInt(dateStr.substring(0, 4));
                                const month = parseInt(dateStr.substring(4, 6)) - 1;
                                const day = parseInt(dateStr.substring(6, 8));
                                date = new Date(year, month, day);
                              } else {
                                date = new Date(dateStr);
                              }
                              
                              const dayOfMonth = date.getDate(); // 1-31
                              
                              // Find maximum available numbered image (using same helper logic)
                              let maxImageNumber = 1;
                              for (let i = 31; i >= 1; i--) {
                                try {
                                  require(`./small-case-images/v${i}.jpg`);
                                  maxImageNumber = i;
                                  break;
                                } catch {
                                  continue;
                                }
                              }
                              
                              // Calculate which image to use: dayOfMonth % maxImageNumber
                              // If result is 0, use maxImageNumber instead
                              let imageNumber = dayOfMonth % maxImageNumber;
                              if (imageNumber === 0) {
                                imageNumber = maxImageNumber;
                              }
                              
                              // Try to load the calculated image, fallback to v1.jpg if it fails
                              let smallImageSrc;
                              try {
                                smallImageSrc = require(`./small-case-images/v${imageNumber}.jpg`);
                              } catch {
                                try {
                                  smallImageSrc = require(`./small-case-images/v1.jpg`);
                                } catch {
                                  return null;
                                }
                              }
                              return (
                                <img 
                                  src={smallImageSrc} 
                                  alt="Case illustration" 
                                  className="float-left mr-3 mb-2 rounded"
                                  style={{ 
                                    maxWidth: '100px',
                                    maxHeight: '100px',
                                    width: 'auto', 
                                    height: 'auto',
                                    objectFit: 'contain'
                                  }}
                                />
                              );
                            } catch {
                              return null;
                            }
                          })()}
                          <div className="text-xs sm:text-sm text-gray-800 leading-relaxed font-oswald" style={{ fontWeight: 'normal' }}>
                          {decodeMojibake(currentCase?.incident?.brief_description) || 'No brief report available.'}
                            {currentCase?.incident && (
                              <>
                                {' '}<span style={{ fontStyle: 'italic' }}>Incident Location: {currentCase.incident.location || 'Unknown'}. Flux rating: {currentCase.incident.Stars !== undefined ? currentCase.incident.Stars : 'Unknown'}. Affinity: {currentCase.incident.Affinity || 'Unknown'}</span>
                              </>
                            )}
                        </div>
                      </div>
                    </div>
                    </div>
                  </div>
                  {/* Dossier Content */}
                  <div className="w-full text-left">
                  </div>
                </>
              )}
              

              {/* Date and Difficulty Badge - only in standard view, hidden for v2 format (shown in metadata card) */}
              {!showClassifiedView && (() => {
                // v2: Hide date/difficulty here if they'll be shown in the metadata card below the image
                const isV2 = currentCase?.incident?.Stars !== undefined && currentCase?.incident?.Affinity !== undefined;
                const hasImage = getCaseImageSrc() !== null;
                
                // Only show standalone date/difficulty if NOT v2 format with image (v2 shows them in metadata card)
                if (isV2 && hasImage) return null;
                
                return (
                  <div className="mb-1 px-4">
                    <div className="flex items-baseline justify-center gap-2 flex-wrap">
                      <h1 className="text-sm font-oswald font-bold text-[#2B2140] text-center leading-tight break-words" style={{
                        fontFamily: "'Oswald', sans-serif",
                        fontWeight: 700,
                        letterSpacing: "0.10em",
                        textTransform: "none"
                      }}>
                        {currentCase?.incident?.date ? formatDateWithDay(currentCase.incident.date) :
                          'Wed 10th Sep 2025'
                        }
                      </h1>
                      {currentCase?.verifiers && (
                        <span
                          className={`px-1.5 py-0.5 rounded-full text-[10px] sm:text-xs font-bold ${
                            (() => {
                              const difficulty = calculateDifficulty(currentCase.verifiers);
                              return difficulty === 'EASY' ? 'bg-green-100 text-green-800' :
                                     difficulty === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800' :
                                     difficulty === 'HARD' ? 'bg-orange-100 text-orange-800' :
                                     'bg-red-100 text-red-800';
                            })()
                          }`}
                        >
                          {calculateDifficulty(currentCase.verifiers)}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })()}
              
              {/* Case Image (if exists) - controlled by Image Mode and hidden in dossier view */}
              {!showClassifiedView && showImageMode && caseImageSrc && (
                <div className="mb-1 w-full flex flex-col items-stretch">
                  <img 
                    ref={imageRef}
                    src={caseImageSrc} 
                    alt="Case illustration" 
                    className="w-full h-auto rounded-lg shadow-md"
                    style={{ maxHeight: imageMaxHeight, objectFit: 'contain' }}
                  />
                </div>
              )}
              
              {/* Progressive Story Display */}
              {!showClassifiedView && (
              <div className="space-y-2 sm:space-y-3 mb-0.5 sm:mb-1 w-full">
                {newGameMode ? (
                  // Single attempt mode: show full story
                  <div className="w-full">
                    <p className="text-gray-800 font-lora leading-relaxed text-justify text-[13px] sm:text-base" style={{ fontWeight: 'normal' }}>
                      {formatInline(decodeMojibake(getDescription(currentCase)))}
                    </p>
                  </div>
                ) : (
                  // Standard mode: progressive story display
                  storySentences.length > 0 && (
                  (!mobileShowEarlier
                    ? [storySentences[storyProgress]]
                    : storySentences.slice(0, storyProgress + 1)
                  ).map((sentence, idx) => {
                    const index = isMobile && !mobileShowEarlier ? storyProgress : idx;
                    return (
                      <div 
                        key={index}
                        className={`p-2 sm:p-3 rounded-lg border-l-4 transition-all duration-500 w-full ${
                          index === storyProgress 
                            ? `${getColorScheme().panel} shadow-md border-l-4 ${getColorScheme().primary.replace('hover:', '').replace('bg-', 'border-')}` // Newly revealed
                            : 'border-gray-300 bg-gray-50' // Previously revealed
                        }`}
                      >
                        <div className="mb-1">
                          <span className="block text-[10px] sm:text-xs font-oswald font-bold text-gray-500">
                              {index === 0 ? 'CASE REPORT:' : `CASE UPDATE ${index}:`}
                          </span>
                        </div>
                        <p className="text-gray-800 font-lora leading-relaxed text-justify text-[13px] sm:text-base" style={{ fontWeight: 'normal' }}>
                          {formatInline(decodeMojibake(sentence))}
                        </p>
                      </div>
                    );
                  })
                  )
              )}

                {/* Mobile: toggle for earlier case updates */}
                {!newGameMode && storyProgress > 0 && (
                  <div className="flex justify-center mt-1">
                    <button
                      onClick={() => setMobileShowEarlier(v => !v)}
                      className={`text-[11px] font-oswald underline ${
                        colorScheme === 0 ? 'text-slate-600' : 
                        colorScheme === 1 ? 'text-purple-400' : 
                        'text-blue-400'
                      }`}
                    >
                      {mobileShowEarlier ? 'Hide earlier case updates' : 'Show earlier case updates'}
                    </button>
                  </div>
                )}
                
                {/* Story Progress Indicator */}
                {!newGameMode && storySentences.length > 1 && (
                  <div className="mt-2 flex items-center gap-2">
                    <div className={`flex-1 h-1 rounded overflow-hidden ${
                      colorScheme === 0 ? 'bg-gray-300' : 
                      colorScheme === 1 ? 'bg-purple-200' : 
                      'bg-blue-200'
                    }`}>
                      <div
                        className={`h-full transition-all ${
                          colorScheme === 0 ? 'bg-slate-600' : 
                          colorScheme === 1 ? 'bg-purple-500' : 
                          'bg-blue-600'
                        }`}
                        style={{ width: `${Math.round(((storyProgress + 1) / storySentences.length) * 100)}%` }}
                      />
                    </div>
                    <span className={`text-[10px] font-oswald whitespace-nowrap ${
                      colorScheme === 0 ? 'text-gray-500' : 
                      colorScheme === 1 ? 'text-purple-300' : 
                      'text-blue-300'
                    }`}>{storyProgress + 1}/{storySentences.length}</span>
                  </div>
                )}
              </div>
              )}
              
              {/* Investigation Notebook Section - only in standard view, above clues */}
              {!showClassifiedView && (
                <div className="px-3 sm:px-6 py-2 bg-gray-100">
                  <div className="w-full h-px bg-gray-300 mb-2"></div>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex-1 text-center">
                      <div className="text-sm sm:text-base font-oswald mb-1 px-3 py-1 rounded uppercase" style={{ fontFamily: "'Oswald', sans-serif", backgroundColor: '#000000', color: '#E8D5B7' }}>
                        <span style={{ fontSize: '0.85em' }}>⚡</span> SOLVE TODAY'S MYSTERY BEFORE REALITY COLLAPSES <span style={{ fontSize: '0.85em' }}>⚡</span>
                    </div>
                    </div>
                    {isAdminMode && (
                      <div className="flex items-center gap-2 ml-2">
                        <button
                          onClick={() => setShowClassifiedView(prev => !prev)}
                          className="px-3 py-1 rounded text-[11px] sm:text-xs font-semibold bg-gray-200 hover:bg-gray-300 text-gray-800 transition-colors whitespace-nowrap"
                        >
                          {showClassifiedView ? 'Standard View' : 'Dossier View'}
                        </button>
                        <button
                          onClick={() => setShowImageMode(prev => !prev)}
                          className="px-3 py-1 rounded text-[11px] sm:text-xs font-semibold bg-gray-200 hover:bg-gray-300 text-gray-800 transition-colors whitespace-nowrap"
                        >
                          {showImageMode ? 'Image Off' : 'Image Mode'}
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="w-full h-px bg-gray-300 mt-1"></div>
                  </div>
              )}

              {/* Field Memo Button - hidden per request */}

              {/* Investigation Notebook Section */}
              {showClassifiedView && (
                <div className="bg-gray-100 mb-0 w-full">
                  <div className="rounded-lg bg-white border border-gray-200 shadow-sm p-2" style={{ margin: 0, width: '100%', boxSizing: 'border-box' }}>
                    <div className="text-center relative">
                      <div className="text-sm sm:text-base font-oswald mb-1 px-3 py-1 rounded uppercase" style={{ fontFamily: "'Oswald', sans-serif", backgroundColor: '#000000', color: '#E8D5B7' }}>
                        <span style={{ fontSize: '0.85em' }}>⚡</span> SOLVE TODAY'S MYSTERY BEFORE REALITY COLLAPSES <span style={{ fontSize: '0.85em' }}>⚡</span>
                  </div>
                      {isAdminMode && getHintsForCase() && (
                        <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowHints(!showHints);
                        }}
                        className="absolute right-0 top-1/2 -translate-y-1/2 hover:opacity-80 active:opacity-60 transition-opacity"
                        title={showHints ? 'Hide Hints' : 'Show Hints'}
                        aria-label={showHints ? 'Hide Hints' : 'Show Hints'}
                        style={{ 
                          width: '36px', 
                          height: '36px', 
                          minWidth: '36px',
                          minHeight: '36px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'transparent',
                          border: 'none',
                          padding: '4px',
                          margin: 0,
                          touchAction: 'manipulation',
                          WebkitTapHighlightColor: 'transparent',
                          boxSizing: 'border-box',
                          cursor: 'pointer',
                          flexShrink: 0
                        }}
                      >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ pointerEvents: 'none', display: 'block', width: '24px', height: '24px' }}>
                          {/* Bulb body - more rounded and bulb-like */}
                          <path d="M12 2C8.13 2 5 5.13 5 9C5 11.38 6.19 13.47 8 14.74V17C8 17.55 8.45 18 9 18H15C15.55 18 16 17.55 16 17V14.74C17.81 13.47 19 11.38 19 9C19 5.13 15.87 2 12 2Z" fill="#FFD700" stroke="#FFA500" strokeWidth="2"/>
                          {/* Base/socket */}
                          <path d="M14 19H10V20C10 20.55 10.45 21 11 21H13C13.55 21 14 20.55 14 20V19Z" fill="#FFA500"/>
                          {/* Highlight/reflection on bulb */}
                          <ellipse cx="10" cy="7" rx="2" ry="3" fill="#FFF9C4" opacity="0.6"/>
                          {/* Filament/light source */}
                          <circle cx="12" cy="9" r="2.5" fill="#FFE082" opacity="0.9"/>
                          <circle cx="12" cy="9" r="1.5" fill="#FFA500" opacity="0.7"/>
                        </svg>
                        </button>
                      )}
                    </div>
                    <div className="flex items-center justify-center gap-2 flex-wrap mt-2 relative" style={{ width: '100%', overflow: 'visible', position: 'relative' }}>
                      <h2 className="text-xs font-oswald uppercase tracking-[0.1em] text-center text-gray-600 flex-1" style={{ fontWeight: 400 }}>
                        APPLY LOGIC TO FIND THE BEING, WEAPON AND HIDEOUT.
                        {currentCase?.verifiers && (
                          <span className="ml-2">
                            DIFFICULTY:{' '}
                            <span
                              className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                                (() => {
                                  const difficulty = calculateDifficulty(currentCase.verifiers);
                                  return difficulty === 'EASY' ? 'bg-green-100 text-green-800' :
                                         difficulty === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800' :
                                         difficulty === 'HARD' ? 'bg-orange-100 text-orange-800' :
                                         'bg-red-100 text-red-800';
                                })()
                              }`}
                            >
                              {calculateDifficulty(currentCase.verifiers)}
                            </span>
                          </span>
                        )}
                      </h2>
                    </div>
                    {/* Clue Reveal Buttons */}
                    <div className="w-full mb-0 mt-4" style={{ overflow: 'visible', position: 'relative' }}>
                      {showClassifiedView && getHintsForCase() && showHints && (() => {
                      const hintsData = getHintsForCase();
                      if (!hintsData) return null;
                      return (
                        <div className="mt-4 mb-4 bg-white bg-opacity-95 rounded-lg p-4 border-2 border-gray-300 shadow-lg relative">
                          <button
                            onClick={() => setShowHints(false)}
                            className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 transition-colors"
                            title="Close hints"
                          >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                            </svg>
                          </button>
                          <div className="space-y-2 max-h-96 overflow-y-auto mb-2">
                            <div className="py-1">
                              <div className="text-xs font-oswald font-bold text-gray-900 mb-1 text-left">
                                HINT {currentHintLevel + 1}
                              </div>
                              <div className="text-xs text-gray-700 mt-1 whitespace-pre-line font-lora leading-relaxed text-left">
                                {hintsData.hints[currentHintLevel]?.hint}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center justify-between">
                            <button
                              onClick={() => setCurrentHintLevel(prev => Math.max(0, prev - 1))}
                              disabled={currentHintLevel === 0}
                              className={`text-xs font-oswald uppercase tracking-wide px-3 py-1 rounded transition-colors ${
                                currentHintLevel === 0
                                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                  : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                              }`}
                            >
                              ← Previous
                            </button>
                            <span className="text-xs text-gray-600 font-oswald">
                              {currentHintLevel + 1} / {hintsData.hints.length}
                            </span>
                            <button
                              onClick={() => setCurrentHintLevel(prev => Math.min(hintsData.hints.length - 1, prev + 1))}
                              disabled={currentHintLevel === hintsData.hints.length - 1}
                              className={`text-xs font-oswald uppercase tracking-wide px-3 py-1 rounded transition-colors ${
                                currentHintLevel === hintsData.hints.length - 1
                                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                  : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                              }`}
                            >
                              Next →
                            </button>
                          </div>
                        </div>
                      );
                    })()}
                <div className="space-y-2">
                  {clueOrder.length > 0 && currentCase.verifiers?.map((_, displayIdx) => {
                    const actualIdx = clueOrder[displayIdx];
                    
                    // Safety check: ensure the actualIdx is valid
                    if (actualIdx === undefined || actualIdx < 0 || actualIdx >= currentCase.verifiers.length) {
                      console.error(`Invalid clueOrder[${displayIdx}]=${actualIdx}, verifiers.length=${currentCase.verifiers.length}`);
                      return null;
                    }
                    
                    const verifier = currentCase.verifiers[actualIdx];
                    const isRevealed = revealedClues.has(actualIdx);
                    console.log(`Display ${displayIdx}: actualIdx=${actualIdx}, isRevealed=${isRevealed}, revealedClues.size=${revealedClues.size}`);
                    return (
                      <div key={displayIdx}>
                        {isRevealed ? (
                          <div 
                            onClick={() => toggleCrossOffClue(actualIdx)}
                            className={`rounded p-2 sm:p-3 border cursor-pointer hover:opacity-80 transition-opacity ${getColorScheme().panel} ${
                              crossedOffClues.has(actualIdx) ? 'opacity-50' : ''
                            }`}
                          >
                <div className={`font-bold text-xs font-oswald tracking-wide whitespace-pre-line flex items-center gap-2 ${
                  colorScheme === 0 ? 'text-slate-800' : 
                  colorScheme === 1 ? 'text-purple-100' : 
                  'text-blue-800'
                            } ${crossedOffClues.has(actualIdx) ? 'line-through' : ''}`}>
                              {/* Inline clue number in a circle */}
                              <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-bold flex-shrink-0 text-gray-800 bg-white border-2 ${
                                colorScheme === 0 ? 'border-slate-700' : 
                                colorScheme === 1 ? 'border-purple-300' : 
                                'border-blue-700'
                              }`}>
                                {displayIdx + 1}
                              </span>
                              <span className="flex-1">
                              {(() => {
                                // Directly evaluate the verifier against the solution to determine what clue to show
                                const solutionBeing = currentCase.components.beings.find(b => 
                                  b.Name.toLowerCase() === currentCase.solution.being.toLowerCase()
                                );
                                const solutionDevice = currentCase.components.devices.find(d => 
                                  d.Name.toLowerCase() === currentCase.solution.device.toLowerCase()
                                );
                                const solutionHideout = currentCase.components.hideouts.find(h => 
                                  h.Name.toLowerCase() === currentCase.solution.hideout.toLowerCase()
                                );
                                
                                if (!solutionBeing || !solutionDevice || !solutionHideout) {
                                  return verifier.Description; // fallback
                                }
                                
                                // Create context for evaluation
                                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                                const context = {
                                  being: {
                                    Stars: solutionBeing.Stars,
                                    Affinity: solutionBeing.Affinity,
                                    Name: solutionBeing.Name.toLowerCase()
                                  },
                                  device: {
                                    Stars: solutionDevice.Stars,
                                    Affinity: solutionDevice.Affinity,
                                    Name: solutionDevice.Name.toLowerCase()
                                  },
                                  hideout: {
                                    Stars: solutionHideout.Stars,
                                    Affinity: solutionHideout.Affinity,
                                    Name: solutionHideout.Name.toLowerCase()
                                  }
                                };
                                
                                // Always use the Description field - it's already correct for UseNegative=true verifiers
                                const clueText = verifier.Description;
                                return enhanceClueWithIncidentValues(clueText, currentCase);
                              })()}
                              </span>
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleRevealClue(actualIdx)}
                            disabled={gameEnded}
                            className={`w-full px-4 py-2 disabled:bg-gray-600 disabled:cursor-not-allowed rounded font-semibold font-oswald transition-colors duration-200 text-white ${
                              colorScheme === 0 ? 'bg-slate-600 hover:bg-slate-700' :
                              colorScheme === 1 ? 'bg-purple-800 hover:bg-purple-900' :
                              'bg-slate-800 hover:bg-slate-900'
                            }`}
                          >
                            {`Reveal Clue #${displayIdx + 1}`}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
                    </div>
                  </div>
                </div>
              )}

              {/* SUSPECTS Heading */}
              {showClassifiedView && (
                <div className="w-full" style={{ marginBottom: 0, paddingBottom: 0, marginTop: 0, lineHeight: 0 }}>
                  <div className="text-sm sm:text-base font-oswald px-3 py-1 rounded uppercase w-full text-center" style={{ fontFamily: "'Oswald', sans-serif", backgroundColor: '#000000', color: '#E8D5B7', marginBottom: 0, marginTop: 0, lineHeight: '1.2', display: 'block' }}>
                    FIND THE BEING, WEAPON AND HIDEOUT
                  </div>
                </div>
              )}

              {/* Clue Reveal Buttons for non-classified view */}
              <div className="w-full mb-4" style={{ overflow: 'visible', position: 'relative' }}>
                {!showClassifiedView && getHintsForCase() && showHints && (() => {
                      const hintsData = getHintsForCase();
                      if (!hintsData) return null;
                      return (
                        <div className="mt-4 mb-4 bg-white bg-opacity-95 rounded-lg p-4 border-2 border-gray-300 shadow-lg relative">
                          <button
                            onClick={() => setShowHints(false)}
                            className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 transition-colors"
                            title="Close hints"
                          >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                            </svg>
                          </button>
                          <div className="space-y-2 max-h-96 overflow-y-auto mb-2">
                            <div className="py-1">
                              <div className="text-xs font-oswald font-bold text-gray-900 mb-1 text-left">
                                HINT {currentHintLevel + 1}
                              </div>
                              <div className="text-xs text-gray-700 mt-1 whitespace-pre-line font-lora leading-relaxed text-left">
                                {hintsData.hints[currentHintLevel]?.hint}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center justify-between">
                            <button
                              onClick={() => setCurrentHintLevel(prev => Math.max(0, prev - 1))}
                              disabled={currentHintLevel === 0}
                              className={`text-xs font-oswald uppercase tracking-wide px-3 py-1 rounded transition-colors ${
                                currentHintLevel === 0
                                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                  : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                              }`}
                            >
                              ← Previous
                            </button>
                            <span className="text-xs text-gray-600 font-oswald">
                              {currentHintLevel + 1} / {hintsData.hints.length}
                            </span>
                            <button
                              onClick={() => setCurrentHintLevel(prev => Math.min(hintsData.hints.length - 1, prev + 1))}
                              disabled={currentHintLevel === hintsData.hints.length - 1}
                              className={`text-xs font-oswald uppercase tracking-wide px-3 py-1 rounded transition-colors ${
                                currentHintLevel === hintsData.hints.length - 1
                                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                  : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                              }`}
                            >
                              Next →
                            </button>
                          </div>
                        </div>
                      );
                    })()}
              </div>

              {/* Solution Checklist */}
              <div className="w-full" style={{ marginTop: '-20px', paddingTop: 0 }}>
                <div className="grid grid-cols-3 gap-2 text-xs" style={{ marginTop: 0, paddingTop: 0 }}>
                  {/* Beings Column */}
                  <div>
                    <div 
                      className="text-black font-oswald font-bold text-md tracking-wide mb-2 text-center uppercase cursor-pointer hover:text-gray-700 transition-colors duration-200 underline decoration-black hover:decoration-gray-700"
                      onClick={() => {
                        if (!showComponents) {
                          setShowComponents(true);
                        }
                        setTimeout(() => beingsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
                      }}
                      title="Click to view detailed information about beings"
                    >
                      Beings
                    </div>
                    <div className="flex flex-col gap-2">
                      {[...(currentCase.components?.beings || [])].sort((a, b) => a.Stars - b.Stars).map(being => {
                        const crossed = crossedOffBeings.includes(being.Name);
                        const selected = selectedBeings.includes(being.Name);
                        return (
                          <button
                            key={being.Name}
                            className={`relative flex flex-col items-center justify-center px-1 py-1 rounded-lg border transition-all duration-150 w-full
                              ${
                                selected
                                  ? `${getColorScheme().selected}`
                                  : crossed
                                    ? 'bg-gray-300 text-gray-700 border-gray-400'
                                    : 'bg-white text-gray-900 border-gray-300'
                              }
                            `}
                            onClick={() => {
                              if (selected) {
                                setSelectedBeings(selectedBeings.filter(n => n !== being.Name));
                              } else {
                                setSelectedBeings([...selectedBeings, being.Name]);
                              }
                              setActionJustTaken(false);
                            }}
                            type="button"
                            style={{ height: '60px', minWidth: '100%' }}
                          >
                            <span className={`font-normal text-sm text-center w-full mb-0.5 tracking-wide truncate-2-lines font-oswald ${selected ? 'selected-grid-name' : ''} ${crossed ? 'line-through' : ''}`}
                              style={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                lineHeight: '1.1em',
                                maxHeight: '2.2em'
                              }}
                            >
                              {being.Name}
                            </span>
                            <div className={`flex items-start justify-start gap-1 w-full text-xs font-normal mt-0.5 ${selected ? 'text-white' : ''}`}>
                              {getAffinityEmoji(being.Affinity)}
                              <span className="text-yellow-600 text-xs">{renderStars(being.Stars)}</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {/* Weapons Column */}
                  <div>
                    <div 
                      className="text-black font-oswald font-bold text-md tracking-wide mb-2 text-center uppercase cursor-pointer hover:text-gray-700 transition-colors duration-200 underline decoration-black hover:decoration-gray-700"
                      onClick={() => {
                        if (!showComponents) {
                          setShowComponents(true);
                        }
                        setTimeout(() => devicesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
                      }}
                      title="Click to view detailed information about weapons"
                    >
                                              Weapons
                    </div>
                    <div className="flex flex-col gap-2">
                      {[...(currentCase.components?.devices || [])].sort((a, b) => a.Stars - b.Stars).map(device => {
                        const crossed = crossedOffDevices.includes(device.Name);
                        const selected = selectedDevices.includes(device.Name);
                        return (
                          <button
                            key={device.Name}
                            className={`relative flex flex-col items-center justify-center px-1 py-1 rounded-lg border transition-all duration-150 w-full
                              ${
                                selected
                                  ? `${getColorScheme().selected}`
                                  : crossed
                                    ? 'bg-gray-300 text-gray-700 border-gray-400'
                                    : 'bg-white text-gray-900 border-gray-300'
                              }
                            `}
                            onClick={() => {
                              if (selected) {
                                setSelectedDevices(selectedDevices.filter(n => n !== device.Name));
                              } else {
                                setSelectedDevices([...selectedDevices, device.Name]);
                              }
                              setActionJustTaken(false);
                            }}
                            type="button"
                            style={{ height: '60px', minWidth: '100%' }}
                          >
                            <span className={`font-normal text-sm text-center w-full mb-0.5 tracking-wide truncate-2-lines font-oswald ${selected ? 'selected-grid-name' : ''} ${crossed ? 'line-through' : ''}`}
                              style={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                lineHeight: '1.1em',
                                maxHeight: '2.2em'
                              }}
                            >
                              {device.Name}
                            </span>
                            <div className={`flex items-start justify-start gap-1 w-full text-xs font-normal mt-0.5 ${selected ? 'text-white' : ''}`}>
                              {getAffinityEmoji(device.Affinity)}
                              <span className="text-yellow-600 text-xs">{renderStars(device.Stars)}</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {/* Hideouts Column */}
                  <div>
                    <div 
                      className="text-black font-oswald font-bold text-md tracking-wide mb-2 text-center uppercase cursor-pointer hover:text-gray-700 transition-colors duration-200 underline decoration-black hover:decoration-gray-700"
                      onClick={() => {
                        if (!showComponents) {
                          setShowComponents(true);
                        }
                        setTimeout(() => hideoutsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
                      }}
                      title="Click to view detailed information about hideouts"
                    >
                      Hideouts
                    </div>
                    <div className="flex flex-col gap-2">
                      {[...(currentCase.components?.hideouts || [])].sort((a, b) => a.Stars - b.Stars).map(hideout => {
                        const crossed = crossedOffHideouts.includes(hideout.Name);
                        const selected = selectedHideouts.includes(hideout.Name);
                        return (
                          <button
                            key={hideout.Name}
                            className={`relative flex flex-col items-center justify-center px-1 py-1 rounded-lg border transition-all duration-150 w-full
                              ${
                                selected
                                  ? `${getColorScheme().selected}`
                                  : crossed
                                    ? 'bg-gray-300 text-gray-700 border-gray-400'
                                    : 'bg-white text-gray-900 border-gray-300'
                              }
                            `}
                            onClick={() => {
                              if (selected) {
                                setSelectedHideouts(selectedHideouts.filter(n => n !== hideout.Name));
                              } else {
                                setSelectedHideouts([...selectedHideouts, hideout.Name]);
                              }
                              setActionJustTaken(false);
                            }}
                            type="button"
                            style={{ height: '60px', minWidth: '100%' }}
                          >
                            <span className={`font-normal text-sm text-center w-full mb-0.5 tracking-wide truncate-2-lines font-oswald ${selected ? 'selected-grid-name' : ''} ${crossed ? 'line-through' : ''}`}
                              style={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                lineHeight: '1.1em',
                                maxHeight: '2.2em'
                              }}
                            >
                              {hideout.Name}
                            </span>
                            <div className={`flex items-start justify-start gap-1 w-full text-xs font-normal mt-0.5 ${selected ? 'text-white' : ''}`}>
                              {getAffinityEmoji(hideout.Affinity)}
                              <span className="text-yellow-600 text-xs">{renderStars(hideout.Stars)}</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
                {/* Checklist Action Buttons */}
                <div className="flex flex-row gap-2 mt-4 justify-center items-center w-full">
                  <button
                    className={`w-1/3 min-h-[3rem] text-xs px-2 py-2 rounded font-semibold font-oswald transition-colors duration-200 shadow-lg text-center
                      ${
                        (gameWon || gameEnded) || actionJustTaken || (selectedBeings.length + selectedDevices.length + selectedHideouts.length === 0)
                          ? 'bg-gray-400 text-white cursor-not-allowed'
                          : `${getColorScheme().primary} text-white`
                      }
                    `}
                    disabled={
                      (gameWon || gameEnded) || actionJustTaken || (selectedBeings.length + selectedDevices.length + selectedHideouts.length === 0)
                    }
                    onClick={() => {
                      // Cross off/uncross off all selected items
                      setCrossedOffBeings(prev => {
                        let next = [...prev];
                        selectedBeings.forEach(name => {
                          if (next.includes(name)) {
                            next = next.filter(n => n !== name);
                          } else {
                            next.push(name);
                          }
                        });
                        return next;
                      });
                      setCrossedOffDevices(prev => {
                        let next = [...prev];
                        selectedDevices.forEach(name => {
                          if (next.includes(name)) {
                            next = next.filter(n => n !== name);
                          } else {
                            next.push(name);
                          }
                        });
                        return next;
                      });
                      setCrossedOffHideouts(prev => {
                        let next = [...prev];
                        selectedHideouts.forEach(name => {
                          if (next.includes(name)) {
                            next = next.filter(n => n !== name);
                          } else {
                            next.push(name);
                          }
                        });
                        return next;
                      });
                      setSelectedBeings([]);
                      setSelectedDevices([]);
                      setSelectedHideouts([]);
                      setActionJustTaken(true);
                    }}
                  >
                    (Un) Eliminate
                  </button>
                  <button
                    className={`w-1/3 min-h-[3rem] text-xs px-2 py-2 rounded font-semibold font-oswald transition-colors duration-200 shadow-lg text-center
                      ${
                        (gameWon || gameEnded) || (failedProposals >= 3) ||
                        !(
                          selectedBeings.filter(b => !crossedOffBeings.includes(b)).length === 1 &&
                          selectedDevices.filter(d => !crossedOffDevices.includes(d)).length === 1 &&
                          selectedHideouts.filter(h => !crossedOffHideouts.includes(h)).length === 1 &&
                          (newGameMode || revealedClues.size >= proposals.length + 1)
                        )
                          ? 'bg-gray-400 text-white cursor-not-allowed'
                          : `${getColorScheme().primary} text-white`
                      }
                    `}
                    disabled={
                      (gameWon || gameEnded) || (failedProposals >= 3) ||
                      !(
                        selectedBeings.filter(b => !crossedOffBeings.includes(b)).length === 1 &&
                        selectedDevices.filter(d => !crossedOffDevices.includes(d)).length === 1 &&
                        selectedHideouts.filter(h => !crossedOffHideouts.includes(h)).length === 1 &&
                        (newGameMode || revealedClues.size >= proposals.length + 1)
                      )
                    }
                    onClick={() => {
                      setConfirmationDialog({
                        show: true,
                        message: 'Are you sure you want to submit this solution?',
                        onConfirm: () => {
                          // Submit proposal using selected items
                          const being = selectedBeings.find(b => !crossedOffBeings.includes(b));
                          const device = selectedDevices.find(d => !crossedOffDevices.includes(d));
                          const hideout = selectedHideouts.find(h => !crossedOffHideouts.includes(h));
                          if (being && device && hideout) {
                            handleSubmitProposalWithTurn({ being, device, hideout });
                            setSelectedBeings([]);
                            setSelectedDevices([]);
                            setSelectedHideouts([]);
                            setActionJustTaken(true);
                          }
                          setConfirmationDialog({ show: false, message: '', onConfirm: () => {} });
                        }
                      });
                    }}
                  >
                    Submit Solution
                  </button>
                  <button
                    className={`w-1/3 min-h-[3rem] text-xs px-2 py-2 rounded font-semibold font-oswald transition-colors duration-200 shadow-lg text-center ${getColorScheme().primary} text-white`}
                    onClick={() => {
                      setConfirmationDialog({
                        show: true,
                        message: 'Are you sure you want to clear all eliminations and retry? This will reset your progress.',
                        onConfirm: () => {
                          setCrossedOffBeings([]);
                          setCrossedOffDevices([]);
                          setCrossedOffHideouts([]);
                          setGameWon(false);
                          setGameEnded(false);
                          setShowSolution(false);
                          setSelectedBeings([]);
                          setSelectedDevices([]);
                          setSelectedHideouts([]);
                          setActionJustTaken(false);
                          setConfirmationDialog({ show: false, message: '', onConfirm: () => {} });
                        }
                      });
                    }}
                  >
                    Clear / Retry
                  </button>
                </div>
                <br></br>
                <div className={`text-xs mb-2 text-center ${
                  colorScheme === 0 ? 'text-slate-700' : 
                  colorScheme === 1 ? 'text-purple-200' : 
                  'text-blue-700'
                }`}>
                  {newGameMode 
                    ? 'select a being, weapon and hideout to submit your solution proposal.'
                    : `reveal at least ${proposals.length + 1} clue${proposals.length + 1 === 1 ? '' : 's'} and select a being, weapon and hideout to submit a new proposal.`
                  }
                </div>
                
                <div className={`w-full flex flex-row flex-wrap justify-center items-center gap-3 mt-1 mb-2 text-xs ${
                  colorScheme === 0 ? 'text-slate-700' : 
                  colorScheme === 1 ? 'text-purple-200' : 
                  'text-blue-700'
                }`}>
                  <div className="flex items-center gap-1"><span role="img" aria-label="Earth">🌍</span> Earth</div>
                  <div className="flex items-center gap-1"><span role="img" aria-label="Saturn">🪐</span> Saturn</div>
                  <div className="flex items-center gap-1"><span role="img" aria-label="Venus">🟡</span> Venus</div>
                  <div className="flex items-center gap-1"><span role="img" aria-label="Mars">🔴</span> Mars</div>
                </div>
                <div className="w-full h-px bg-gray-300 mt-1 mb-2"></div>

                {/* PAR Display Block moved under affinity/help text */}
                {!newGameMode && (
                <div className={`rounded-lg p-4 border mb-4 w-full shadow-lg ${getColorScheme().panel}`}>
                  <div className="flex items-center justify-center gap-2 mb-2 relative">
                    {[...Array(8)].map((_, i) => {
                    const par = currentCase.verifiers.length + 1;
                      return (
                        <div key={i} className="relative">
                          <div
                            className={`w-3 h-3 rounded-full border-2 ${
                              i < turnCount
                                ? colorScheme === 0 ? 'bg-slate-600 border-slate-300' :
                                  colorScheme === 1 ? 'bg-purple-600 border-slate-300' :
                                  'bg-blue-600 border-slate-300'
                                : 'bg-slate-400 border-slate-300'
                            }`}
                          />
                          {i === par - 1 && (
                            <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-0.5 bg-slate-300"></div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="text-center">
                  <div className="text-gray-800 font-oswald font-bold text-sm">
                    PAR: {currentCase.verifiers.length + 1} MOVES
                    </div>
                    <div className={`font-lora text-xs mt-1 ${
                      colorScheme === 0 ? 'text-slate-700' : 
                      colorScheme === 1 ? 'text-purple-700' : 
                      'text-blue-700'
                    }`}>
                      EACH CLUE REVEAL AND PROPOSAL COUNTS AS A MOVE
                      {!freeProposalUsed && revealedClues.size <= 2 && (
                        <div className={`font-semibold mt-1 ${
                          colorScheme === 0 ? 'text-slate-500' : 
                          colorScheme === 1 ? 'text-purple-400' : 
                          'text-blue-500'
                        }`}>
                          FREE PROPOSAL AVAILABLE UNTIL 3 CLUES REVEALED!
                        </div>
                      )}
                      {freeProposalUsed && (
                        <div className="text-gray-500 font-semibold mt-1">
                          FREE PROPOSAL USED
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                )}
              
            </div>
</div>
            {/* Proposals Section */}
            {/* Always render the card layout for proposal results, even on desktop */}
            <div className="space-y-6 pb-0">
              {/* Proposal Input Card - commented out, use checklist instead. Restore if needed in future.
                <div className="bg-gray-800 bg-opacity-50 rounded-lg p-4 flex flex-col gap-2 border border-purple-400 w-full">
                  <button
                    onClick={() => handleSubmitProposalWithTurn()}
                    disabled={!currentProposal.being || !currentProposal.device || !currentProposal.hideout || gameEnded}
                    className={`w-full px-8 py-3 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg font-semibold transition-colors duration-200 shadow-lg text-lg mb-2 text-white ${getColorScheme().primary}`}
                  >
                    <span className="font-oswald">Submit Proposal #{proposals.length + 1}</span>
                  </button>
                  <select
                    value={currentProposal.being}
                    onChange={(e) => setCurrentProposal({...currentProposal, being: e.target.value})}
                    className="bg-gray-800 border border-gray-600 rounded px-3 py-3 text-white w-full text-base block mb-2"
                  >
                    <option value="">Select Being</option>
                    {currentCase.components.beings
                      .slice()
                      .sort((a, b) => a.Stars - b.Stars)
                      .map((being, index) => (
                        <option key={index} value={being.Name} className="text-base">
                          {getAffinityEmoji(being.Affinity)} {renderStars(being.Stars)} {being.Name}
                        </option>
                      ))}
                  </select>
                  <select
                    value={currentProposal.device}
                    onChange={(e) => setCurrentProposal({...currentProposal, device: e.target.value})}
                    className="bg-gray-800 border border-gray-600 rounded px-3 py-3 text-white w-full text-base block mb-2"
                  >
                    <option value="">Select Weapon</option>
                    {currentCase.components.devices
                      .slice()
                      .sort((a, b) => a.Stars - b.Stars)
                      .map((device, index) => (
                        <option key={index} value={device.Name} className="text-base">
                          {getAffinityEmoji(device.Affinity)} {renderStars(device.Stars)} {device.Name}
                        </option>
                      ))}
                  </select>
                  <select
                    value={currentProposal.hideout}
                    onChange={(e) => setCurrentProposal({...currentProposal, hideout: e.target.value})}
                    className="bg-gray-800 border border-gray-600 rounded px-3 py-3 text-white w-full text-base block"
                  >
                    <option value="">Select Hideout</option>
                    {currentCase.components.hideouts
                      .slice()
                      .sort((a, b) => a.Stars - b.Stars)
                      .map((hideout, index) => (
                        <option key={index} value={hideout.Name} className="text-base">
                          {getAffinityEmoji(hideout.Affinity)} {renderStars(hideout.Stars)} {hideout.Name}
                        </option>
                      ))}
                  </select>
                </div>
                */}
              {/* Submitted Proposals as Cards */}
              <div ref={proposalsRef} className="mt-3">
              {[...proposals].reverse().map((proposalResult, pIdx) => {
                const beingObj = currentCase.components?.beings?.find(b => b.Name === proposalResult.proposal.being);
                const deviceObj = currentCase.components?.devices?.find(d => d.Name === proposalResult.proposal.device);
                const hideoutObj = currentCase.components?.hideouts?.find(h => h.Name === proposalResult.proposal.hideout);
                return (
                  <div key={pIdx} className={`proposal-card rounded-lg p-4 flex flex-col gap-2 border shadow-none ${pIdx < proposals.length - 1 ? 'mb-3' : 'mb-0'} ${getColorScheme().panel}`}>
                    {/* Headings and values row as a single grid, full width, left-aligned */}
                    <div className="w-full">
                      {/* Proposal number above headings for alignment */}
                      <div className="mb-1">
                        <span className={`font-bold text-left font-oswald ${
                          colorScheme === 0 ? 'text-slate-700' : 
                          colorScheme === 1 ? 'text-purple-200' : 
                          'text-blue-700'
                        }`}>
                          
                          {' '}PROPOSAL {proposals.length - pIdx}
                        </span>
                      </div>
                     
                      <div className="grid grid-cols-3 gap-2 justify-center items-start mt-2">
  {/* Being */}
  {beingObj ? (
    <div className="flex flex-col items-center">
                            <div className={`text-xs font-bold font-oswald uppercase mb-1 flex items-center gap-1 ${
                              colorScheme === 0 ? 'text-slate-700' : 
                              colorScheme === 1 ? 'text-purple-200' : 
                              'text-blue-700'
                            }`}>
                        BEING <span className={`inline-block px-1 py-0.5 rounded text-xs font-bold ml-1 ${proposalResult.proposal.being === currentCase.solution.being ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>{proposalResult.proposal.being === currentCase.solution.being ? '✓' : '✗'}</span>
                      </div>
      <button disabled className="relative flex flex-col items-center justify-center px-1 py-1 rounded-lg border transition-all duration-150 w-full bg-white text-gray-900 border-gray-300 cursor-default" style={{ height: '60px', minWidth: '100%' }}>
        <span className="text-xs text-center w-full mb-0.5 tracking-wide truncate-2-lines font-oswald font-normal normal-case" style={{
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          lineHeight: '1.1em',
          maxHeight: '2.2em',
          textTransform: 'none'
        }}>{beingObj.Name}</span>
        <div className="flex items-start justify-start gap-1 w-full text-xs font-normal mt-0.5">
          {getAffinityEmoji(beingObj.Affinity)}
          <span className="text-yellow-600 text-xs">{renderStars(beingObj.Stars)}</span>
        </div>
      </button>
    </div>
  ) : <></>}
  {/* Weapon */}
  {deviceObj ? (
    <div className="flex flex-col items-center">
      <div className={`text-xs font-bold font-oswald uppercase mb-1 flex items-center gap-1 ${
        colorScheme === 0 ? 'text-slate-700' : 
        colorScheme === 1 ? 'text-purple-200' : 
        'text-blue-700'
      }`}>
                            WEAPON <span className={`inline-block px-1 py-0.5 rounded text-xs font-bold ml-1 ${proposalResult.proposal.device === currentCase.solution.device ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>{proposalResult.proposal.device === currentCase.solution.device ? '✓' : '✗'}</span>
      </div>
      <button disabled className="relative flex flex-col items-center justify-center px-1 py-1 rounded-lg border transition-all duration-150 w-full bg-white text-gray-900 border-gray-300 cursor-default" style={{ height: '60px', minWidth: '100%' }}>
        <span className="text-xs text-center w-full mb-0.5 tracking-wide truncate-2-lines font-oswald font-normal normal-case" style={{
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          lineHeight: '1.1em',
          maxHeight: '2.2em',
          textTransform: 'none'
        }}>{deviceObj.Name}</span>
        <div className="flex items-start justify-start gap-1 w-full text-xs font-normal mt-0.5">
          {getAffinityEmoji(deviceObj.Affinity)}
          <span className="text-yellow-600 text-xs">{renderStars(deviceObj.Stars)}</span>
        </div>
      </button>
    </div>
  ) : <></>}
  {/* Hideout */}
  {hideoutObj ? (
    <div className="flex flex-col items-center">
      <div className={`text-xs font-bold font-oswald uppercase mb-1 flex items-center gap-1 ${
        colorScheme === 0 ? 'text-slate-700' : 
        colorScheme === 1 ? 'text-purple-200' : 
        'text-blue-700'
      }`}>
        HIDEOUT <span className={`inline-block px-1 py-0.5 rounded text-xs font-bold ml-1 ${proposalResult.proposal.hideout === currentCase.solution.hideout ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>{proposalResult.proposal.hideout === currentCase.solution.hideout ? '✓' : '✗'}</span>
      </div>
      <button disabled className="relative flex flex-col items-center justify-center px-1 py-1 rounded-lg border transition-all duration-150 w-full bg-white text-gray-900 border-gray-300 cursor-default" style={{ height: '60px', minWidth: '100%' }}>
        <span className="text-xs text-center w-full mb-0.5 tracking-wide truncate-2-lines font-oswald font-normal normal-case" style={{
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          lineHeight: '1.1em',
          maxHeight: '2.2em',
          textTransform: 'none'
        }}>{hideoutObj.Name}</span>
        <div className="flex items-start justify-start gap-1 w-full text-xs font-normal mt-0.5">
          {getAffinityEmoji(hideoutObj.Affinity)}
          <span className="text-yellow-600 text-xs">{renderStars(hideoutObj.Stars)}</span>
        </div>
      </button>
    </div>
  ) : <></>}
</div>
                    </div>

                  </div>
                );
              })}
              </div>
            </div>
          </div>

          {/* Components Toggle Button */}
          {/* <div className="flex justify-center mb-4">
            <button
              onClick={() => setShowComponents(!showComponents)}
              className={`px-6 py-3 rounded-lg font-semibold text-white shadow-lg transition-colors duration-200 ${getColorScheme().primary}`}
            >
              {showComponents ? 'Hide' : 'Show'} More Information About the Possible Solutions in This Case
            </button>
          </div> */}

          {/* Components Grid */}
          {showComponents && (
            <div className="flex flex-col gap-6 mb-8 sm:gap-2 sm:mb-2 gap-2 mb-4 md:gap-4 md:mb-6 mt-0">
            {/* Beings */}
            <div ref={beingsRef} className="components-beings bg-black bg-opacity-30 rounded-lg p-4 border border-purple-500 sm:p-2 p-2 md:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-oswald font-semibold text-purple-300">Beings</h3>
              </div>
              <div className="space-y-2">
                {currentCase.components.beings
                  .slice()
                  .sort((a, b) => a.Stars - b.Stars)
                  .map((being, index) => (
                    <div
                      key={index}
                      className={`bg-white rounded p-3 border border-gray-300 transition-all duration-200 cursor-pointer ${crossedOffBeings.includes(being.Name) ? 'crossed-off' : ''}`}
                      onClick={() => toggleCrossOffBeing(being.Name)}
                      title={crossedOffBeings.includes(being.Name) ? 'Uncross' : 'Cross off'}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-sm font-semibold ${crossedOffBeings.includes(being.Name) ? 'line-through text-gray-500' : 'text-gray-900'}`}>{being.Name}</span>
                        <span className="text-xs text-yellow-600">{renderStars(being.Stars)}</span>
                      </div>
                      <div className="flex items-center justify-between text-xs text-gray-700">
                        <span>{getAffinityEmoji(being.Affinity)} {being.Affinity}</span>
                        <span>{being.Type}</span>
                      </div>
                      <p className={`text-xs mt-1 ${crossedOffBeings.includes(being.Name) ? 'line-through text-gray-400' : 'text-gray-800'}`}>{decodeMojibake(being.Description)}</p>
                    </div>
                  ))}
              </div>
            </div>

            {/* Weapons */}
            <div ref={devicesRef} className="components-weapons bg-black bg-opacity-30 rounded-lg p-4 border border-purple-500 sm:p-2 p-2 md:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-oswald font-semibold text-purple-300">Weapons</h3>
              </div>
              <div className="space-y-2">
                {currentCase.components.devices
                  .slice()
                  .sort((a, b) => a.Stars - b.Stars)
                  .map((device, index) => (
                    <div
                      key={index}
                      className={`bg-white rounded p-3 border border-gray-300 transition-all duration-200 cursor-pointer ${crossedOffDevices.includes(device.Name) ? 'crossed-off' : ''}`}
                      onClick={() => toggleCrossOffDevice(device.Name)}
                      title={crossedOffDevices.includes(device.Name) ? 'Uncross' : 'Cross off'}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-sm font-semibold ${crossedOffDevices.includes(device.Name) ? 'line-through text-gray-500' : 'text-gray-900'}`}>{device.Name}</span>
                        <span className="text-xs text-yellow-600">{renderStars(device.Stars)}</span>
                      </div>
                      <div className="flex items-center justify-between text-xs text-gray-700">
                        <span>{getAffinityEmoji(device.Affinity)} {device.Affinity}</span>
                        <span>{device.Type}</span>
                      </div>
                      <p className={`text-xs mt-1 ${crossedOffDevices.includes(device.Name) ? 'line-through text-gray-400' : 'text-gray-800'}`}>{decodeMojibake(device.Description)}</p>
                    </div>
                  ))}
              </div>
            </div>

            {/* Hideouts */}
            <div ref={hideoutsRef} className="components-hideouts bg-black bg-opacity-30 rounded-lg p-4 border border-purple-500 sm:p-2 p-2 md:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-oswald font-semibold text-purple-300">Hideouts</h3>
              </div>
              <div className="space-y-2">
                {currentCase.components.hideouts
                  .slice()
                  .sort((a, b) => a.Stars - b.Stars)
                  .map((hideout, index) => (
                    <div
                      key={index}
                      className={`bg-white rounded p-3 border border-gray-300 transition-all duration-200 cursor-pointer ${crossedOffHideouts.includes(hideout.Name) ? 'crossed-off' : ''}`}
                      onClick={() => toggleCrossOffHideout(hideout.Name)}
                      title={crossedOffHideouts.includes(hideout.Name) ? 'Uncross' : 'Cross off'}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-sm font-semibold ${crossedOffHideouts.includes(hideout.Name) ? 'line-through text-gray-500' : 'text-gray-900'}`}>{hideout.Name}</span>
                        <span className="text-xs text-yellow-600">{renderStars(hideout.Stars)}</span>
                      </div>
                      <div className="flex items-center justify-between text-xs text-gray-700">
                        <span>{getAffinityEmoji(hideout.Affinity)} {hideout.Affinity}</span>
                      </div>
                      <p className={`text-xs mt-1 ${crossedOffHideouts.includes(hideout.Name) ? 'line-through text-gray-400' : 'text-gray-800'}`}>{decodeMojibake(hideout.Description)}</p>
                    </div>
                    
                  ))}
              </div>
              
            </div>
            
          </div>
          )}

          {/* No Case Available Message */}
          {!isAdminMode && !currentCase && (
            <div className="bg-white bg-opacity-95 rounded-lg p-6 border-2 border-gray-300 shadow-lg mb-6 w-full text-center">
              <h3 className="text-xl font-oswald font-bold text-gray-900 mb-4">
                No Case Available Today
              </h3>
              <p className="text-gray-700 font-lora">
                There is no case available for today. Please check back tomorrow for a new mystery to solve!
              </p>
            </div>
          )}

          {/* Bottom Action Buttons */}
          {isAdminMode && (
            <div className="flex flex-col gap-2 mt-0 mb-3 justify-center items-center w-full">
            {/* Continue Game Button - Admin only */}
              {!currentCase && GameStateService.hasSavedGame() && (
              <div className="w-full mb-2">
                <button
                  onClick={() => {
                    const savedState = GameStateService.loadGameState();
                    if (savedState) {
                      console.log('Continue game - revealedClues:', savedState.revealedClues);
                      
                      isLoadingRef.current = true;
                      
                      setCurrentCase(savedState.currentCase);
                      setProposals(savedState.proposals || []);
                      setCurrentProposal(savedState.currentProposal || { being: '', device: '', hideout: '' });
                      setShowSolution(savedState.showSolution || false);
                      setGameWon(savedState.gameWon || false);
                      setCrossedOffBeings(savedState.crossedOffBeings || []);
                      setCrossedOffDevices(savedState.crossedOffDevices || []);
                      setCrossedOffHideouts(savedState.crossedOffHideouts || []);
                      setCrossedOffClues(new Set(savedState.crossedOffClues || []));
                      setTurnCount(savedState.turnCount || 0);
                      setRevealedClues(new Set(savedState.revealedClues || []));
                      setFieldMemoRevealed(savedState.fieldMemoRevealed || false);
                      setGameEnded(savedState.gameEnded || false);
                      setFreeProposalUsed(savedState.freeProposalUsed || false);
                      setClueOrder(savedState.clueOrder || []);
                      setMobileShowEarlier(savedState.mobileShowEarlier || false);
                      
                      // Track continued game
                      if (savedState.currentCase) {
                        analytics.trackUserInteraction('game_continued', {
                          date: savedState.currentCase.incident?.date,
                          case_id: savedState.currentCase.case_id,
                          turn_count: savedState.turnCount
                        });
                      }
                      
                      // Reset the loading flag after a short delay
                      setTimeout(() => {
                        isLoadingRef.current = false;
                      }, 100);
                    }
                  }}
                  className="w-full px-4 py-3 bg-green-600 hover:bg-green-700 rounded-lg font-semibold transition-colors duration-200 shadow-lg text-white text-center"
                >
                  Continue Game
                  {(() => {
                    const savedInfo = GameStateService.getSavedGameInfo();
                    if (savedInfo) {
                      return (
                        <div className="text-xs mt-1 opacity-80">
                          {formatDateWithDay(savedInfo.date)} • Turn {savedInfo.turnCount}
                        </div>
                      );
                    }
                    return null;
                  })()}
                </button>
              </div>
            )}
            
            {/* Admin-only buttons */}
              <div className="space-y-2">
                {/* Admin mode indicator */}
                <div className="w-full px-3 py-2 bg-yellow-600 rounded-lg border-2 border-yellow-400 text-center">
                  <span className="text-white font-bold text-sm">🔧 ADMIN MODE - Images Enabled</span>
                </div>
                <div className="flex flex-row gap-2 w-full">
                  <button
                    onClick={() => setShowNewCaseModal(true)}
                    className={`w-1/2 px-4 py-3 rounded-lg font-semibold transition-colors duration-200 shadow-lg text-white text-center ${getColorScheme().primary}`}
                  >
                    New Game
                  </button>
                  <button
                    onClick={handleRevealSolution}
                    className={`w-1/2 px-4 py-3 rounded-lg font-semibold transition-colors duration-200 shadow-lg text-white text-center ${getColorScheme().primary}`}
                  >
                    Solution
                  </button>
                </div>
              </div>
          </div>
          )}

          {/* Solution Display */}
          {(showSolution || gameWon) && currentCase && (
            <div ref={revealCardRef} className="bg-white bg-opacity-95 rounded-lg p-6 border-2 border-gray-300 mt-3 mb-3 w-full">
              <div className="text-center mb-4">
                <h3 className="text-base font-oswald font-bold text-gray-600 mb-2">
                  BUREAU GAMMA CASE RESOLUTION
                </h3>
                <h4 className="text-sm font-oswald text-gray-500 mb-3">
                  FINAL SOLUTION REPORT
                </h4>
                <div className="w-16 h-px bg-gray-400 mx-auto mb-4"></div>
              </div>
              
              <h3 className="text-2xl font-oswald font-bold text-gray-900 mb-4 text-center leading-tight">
                {gameWon ? '🎉 CASE SOLVED! 🎉' : (gameEnded && !gameWon ? 'Game Over - Solution Revealed' : 'Solution Revealed')}
              </h3>
              
              {gameWon && (
                <p className="mb-3 font-lora text-center text-gray-900">
                  {newGameMode ? (
                    <>Case solved!</>
                  ) : (
                    <>
                  Solved in <span>{turnCount}</span> moves
                  {(() => {
                    const par = currentCase.verifiers.length + 1;
                    const difference = turnCount - par;
                    if (difference === 0) {
                      return <span> (PAR!)</span>;
                    } else if (difference < 0) {
                      return <span> ({Math.abs(difference)} under par)</span>;
                    } else {
                      return <span> ({difference} over par)</span>;
                    }
                  })()}
                    </>
                  )}
                  {caseStartTimeRef.current && (
                    <>
                      <br className="sm:hidden" />
                      <span className="hidden sm:inline"> • </span>
                      <span className="font-lora text-sm">
                        {(() => {
                          const elapsedMs = Date.now() - (caseStartTimeRef.current as number);
                          const elapsedMinutes = Math.max(1, Math.ceil(elapsedMs / 60000));
                          return `Solved in less than ${elapsedMinutes} minute${elapsedMinutes === 1 ? '' : 's'}!`;
                        })()}
                      </span>
                    </>
                  )}
                </p>
              )}
              
              {gameEnded && !gameWon && (
                <p className="text-black mb-4 font-lora text-center">
                  {newGameMode && proposals.length > 0 ? (
                    <>
                      Your proposal of <strong>{proposals[proposals.length - 1].proposal.being}</strong>, <strong>{proposals[proposals.length - 1].proposal.device}</strong>, and <strong>{proposals[proposals.length - 1].proposal.hideout}</strong> was not correct. The solution is revealed below.
                    </>
                  ) : (
                    <>
                      You've reached the maximum number of moves. The solution is revealed below.
                    </>
                  )}
                </p>
              )}
              
              <div className="w-full h-px bg-gray-300 mb-4"></div>
              
              {/* Solution Text - Show when available, otherwise show case description */}
              {currentCase.solution_text ? (
                <div className="bg-white bg-opacity-95 rounded-lg p-6 border-2 border-gray-300 shadow-lg mb-6 w-full">
                  <div className="text-center mb-4">
                                    <h3 className="text-sm font-oswald font-bold text-gray-600 mb-1">
                  CASE RESOLUTION
                </h3>
                <h4 className="text-xs font-oswald text-gray-500 mb-2">
                  FINAL REPORT
                </h4>
                    <div className="w-16 h-px bg-gray-400 mx-auto mb-3"></div>
                  </div>
                  
                  <div className="text-gray-800 font-lora leading-relaxed text-justify">
                    {decodeMojibake(currentCase.solution_text)}
                  </div>
                </div>
              ) : (
                <div className="bg-white bg-opacity-95 rounded-lg p-6 border-2 border-gray-300 shadow-lg mb-6 w-full">
                  <div className="text-center mb-4">
                                    <h3 className="text-sm font-oswald font-bold text-gray-600 mb-1">
                  CASE BRIEFING
                </h3>
                <h4 className="text-xs font-oswald text-gray-500 mb-2">
                  INCIDENT SUMMARY
                </h4>
                    <div className="w-16 h-px bg-gray-400 mx-auto mb-3"></div>
                  </div>
                  
                  <h4 className="text-xl font-oswald font-bold text-gray-900 mb-4 text-center leading-tight">Case Details</h4>
                  
                  <div className="w-full h-px bg-gray-300 mb-4"></div>
                  
                  <div className="text-gray-800 font-lora leading-relaxed text-justify">
                    <div className="font-semibold text-lg mb-3 text-center">{decodeMojibake(getHeadline(currentCase))}</div>
                    <div className="text-sm leading-relaxed">
                      {formatInline(decodeMojibake(getDescription(currentCase)))}
                    </div>
                  </div>
                </div>
              )}
              
              <div className="flex flex-col gap-6">
                {(() => {
                  const beingObj = currentCase.components.beings.find(b => b.Name === currentCase.solution.being);
                  return (
                    <div className="solution-being bg-white bg-opacity-95 rounded-lg p-1.5 border-2 border-gray-300 shadow-lg text-left">
                      <div className="text-sm font-oswald text-gray-900 mb-1 normal-case">Being: {currentCase.solution.being}</div>
                      {beingObj && (
                        <>
                          <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                            <span className="flex items-center gap-1">
                              <span>{getAffinityEmoji(beingObj.Affinity)}</span>
                              <span className="font-oswald">{beingObj.Affinity}</span>
                            </span>
                            <span className="text-yellow-600 font-semibold">{renderStars(beingObj.Stars)}</span>
                          </div>
                          <div className="text-xs text-gray-700 italic leading-tight font-lora line-clamp-4">
                            {decodeMojibake(beingObj.Description)}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })()}
                {(() => {
                  const deviceObj = currentCase.components.devices.find(d => d.Name === currentCase.solution.device);
                  return (
                    <div className="solution-weapon bg-white bg-opacity-95 rounded-lg p-1.5 border-2 border-gray-300 shadow-lg text-left">
                      <div className="text-sm font-oswald text-gray-900 mb-1 normal-case">Weapon: {currentCase.solution.device}</div>
                      {deviceObj && (
                        <>
                          <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                            <span className="flex items-center gap-1">
                              <span>{getAffinityEmoji(deviceObj.Affinity)}</span>
                              <span className="font-oswald">{deviceObj.Affinity}</span>
                            </span>
                            <span className="text-yellow-600 font-semibold">{renderStars(deviceObj.Stars)}</span>
                          </div>
                          <div className="text-xs text-gray-700 italic leading-tight font-lora line-clamp-4">
                            {decodeMojibake(deviceObj.Description)}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })()}
                {(() => {
                  const hideoutObj = currentCase.components.hideouts.find(h => h.Name === currentCase.solution.hideout);
                  return (
                    <div className="solution-hideout bg-white bg-opacity-95 rounded-lg p-1.5 border-2 border-gray-300 shadow-lg text-left">
                      <div className="text-sm font-oswald text-gray-900 mb-1 normal-case">Hideout: {currentCase.solution.hideout}</div>
                      {hideoutObj && (
                        <>
                          <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                            <span className="flex items-center gap-1">
                              <span>{getAffinityEmoji(hideoutObj.Affinity)}</span>
                              <span className="font-oswald">{hideoutObj.Affinity}</span>
                            </span>
                            <span className="text-yellow-600 font-semibold">{renderStars(hideoutObj.Stars)}</span>
                          </div>
                          <div className="text-xs text-gray-700 italic leading-tight font-lora line-clamp-4">
                            {decodeMojibake(hideoutObj.Description)}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })()}
                        </div>
        </div>
      )}

      {/* Book banner moved above Newsletter Signup (shown in all modes) - HIDDEN (can be restored later) */}
      {/* <div className="w-full md:max-w-xl mx-auto mb-3 mt-2">
        <Link to="/book" className="block">
          <div className="flex flex-row items-stretch bg-white/95 border border-slate-200 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-200 overflow-hidden">
            <img src={book0} alt="Dimension Flux Book" className="h-full w-16 sm:w-20 md:w-24 object-cover flex-shrink-0" />
            <div className="flex-1 flex flex-col justify-center min-w-0 p-2 sm:p-3 md:p-4">
              <h3 className="text-xs sm:text-sm md:text-base lg:text-lg font-oswald font-bold text-slate-900 leading-tight mb-1 sm:mb-2.5 whitespace-nowrap overflow-hidden text-ellipsis">
                50 NEW DIMENSION FLUX PUZZLES TO TAKE OFFLINE.
              </h3>
              <span className="text-xs sm:text-xs md:text-sm text-slate-700 font-oswald uppercase tracking-wide leading-tight whitespace-nowrap overflow-hidden text-ellipsis">
                LEARN ABOUT THE DIMENSION FLUX BOOK
              </span>
            </div>
          </div>
        </Link>
      </div> */}

      {/* Newsletter Signup */}
      <div className="mt-1 mb-1">
        <MailerLiteSignup colorScheme={colorScheme} />
      </div>

        </div>
      </div>
      
      {/* Quick Mode card moved to bottom */}
      {/* <div className="mb-6 w-full">
        <div className="bg-slate-900 bg-opacity-30 rounded p-4 border border-slate-400 w-full">
          <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-2">
            <div className="text-blue-200 font-oswald font-bold text-md tracking-wide text-center">
              <div>{quickMode ? 'QUICK MODE' : 'STANDARD MODE'}</div>
              <div className="text-xs text-gray-300 mt-1 font-lora">
                {quickMode ?
                  "You'll be told whether each element in your proposal is correct" :
                  "Use logic tests alone to deduce the solution"
                }
              </div>
            </div>
            <button
              onClick={() => setQuickMode(!quickMode)}
              className={`px-6 py-3 rounded-lg font-semibold transition-colors duration-200 shadow-lg text-white ${getColorScheme().primary}`}
            >
              Switch to {quickMode ? 'Standard' : 'Quick'} Mode
            </button>
          </div>
        </div>
      </div> */}
      
      {showWinModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
          <div className="bg-white bg-opacity-95 rounded-2xl shadow-2xl p-3 max-w-lg w-full text-center relative max-h-[85vh] border-2 border-gray-300 flex flex-col">
            {/* Mobile buttons at top */}
            <div className="flex gap-2 justify-center mb-3 pb-3 border-b border-gray-200 sm:hidden">
              {/* Only show share button on mobile devices */}
              {/Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) && (
                <button
                  className={`px-6 py-2 text-white rounded-lg font-semibold shadow font-oswald flex items-center gap-2 ${getColorScheme().primary}`}
                  onClick={handleShareResult}
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z"/>
                  </svg>
                  SHARE RESULT
                </button>
              )}
              <button
                className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-semibold shadow font-oswald"
                onClick={() => {
                  setShowWinModal(false);
                  // Scroll to the top of the page after a short delay to ensure modal is closed
                  setTimeout(() => {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }, 100);
                }}
              >
                CLOSE
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto space-y-4">
              {SHARE_PANEL_UPDATES_ENABLED &&
                renderShareCard({
                  showButton: true,
                  onShareClick: () => setShowShareSheet(true)
                })}
                            <h2 className="text-2xl font-oswald font-bold text-gray-900">🎉 CASE SOLVED! 🎉</h2>
            <p className="mb-3 text-gray-800 font-lora text-base">
              {newGameMode ? (
                <>Case solved!</>
              ) : (
                <>
              Solved in <span>{turnCount}</span> moves
              {currentCase && (() => {
                const par = currentCase.verifiers.length + 1;
                const difference = turnCount - par;
                if (difference === 0) {
                  return <span> (PAR!)</span>;
                } else if (difference < 0) {
                  return <span> ({Math.abs(difference)} under par)</span>;
                } else {
                  return <span> ({difference} over par)</span>;
                }
              })()}
                </>
              )}
              {caseStartTimeRef.current && (
                <>
                  <br className="sm:hidden" />
                  <span className="hidden sm:inline"> • </span>
                  <span className="font-lora">
                    {(() => {
                      const elapsedMs = Date.now() - (caseStartTimeRef.current as number);
                      const elapsedMinutes = Math.max(1, Math.ceil(elapsedMs / 60000));
                      return `Solved in less than ${elapsedMinutes} minute${elapsedMinutes === 1 ? '' : 's'}!`;
                    })()}
                  </span>
                </>
              )}
            </p>
            
            {/* Solution Text */}
            {currentCase?.solution_text && (
              <div className="mb-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="text-sm text-gray-600 font-oswald font-bold mb-2 text-center">CASE RESOLUTION</div>
                <div className="text-sm text-gray-700 font-lora leading-relaxed text-justify">
                  {decodeMojibake(currentCase.solution_text)}
                </div>
              </div>
            )}
            
            <div className="w-full h-px bg-gray-300 mb-3"></div>
            
            {/* Solution Components */}
            <div className="flex flex-col gap-2 mb-3">
              {(() => {
                const beingObj = currentCase.components.beings.find(b => b.Name === currentCase.solution.being);
                return (
                  <div className="solution-being-win bg-white bg-opacity-95 rounded-lg p-3 border-2 border-gray-300 shadow-lg text-left">

                    <div className="text-base font-oswald font-bold text-gray-900 mb-2 normal-case">Being: {currentCase.solution.being}</div>
                    {beingObj && (
                      <>
                        <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                          <span className="flex items-center gap-1">
                            <span>{getAffinityEmoji(beingObj.Affinity)}</span>
                            <span className="font-oswald font-semibold">{beingObj.Affinity}</span>
                          </span>
                          <span className="text-yellow-600 font-bold">{renderStars(beingObj.Stars)}</span>
                        </div>
                        <div className="text-sm text-gray-700 italic leading-relaxed font-lora line-clamp-4">
                          {decodeMojibake(beingObj.Description)}
                        </div>
                      </>
                    )}
                  </div>
                );
              })()}
              {(() => {
                const deviceObj = currentCase.components.devices.find(d => d.Name === currentCase.solution.device);
                return (
                  <div className="solution-weapon-win bg-white bg-opacity-95 rounded-lg p-3 border-2 border-gray-300 shadow-lg text-left">

                    <div className="text-base font-oswald font-bold text-gray-900 mb-2 normal-case">Weapon: {currentCase.solution.device}</div>
                    {deviceObj && (
                      <>
                        <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                          <span className="flex items-center gap-1">
                            <span>{getAffinityEmoji(deviceObj.Affinity)}</span>
                            <span className="font-oswald font-semibold">{deviceObj.Affinity}</span>
                          </span>
                          <span className="text-yellow-600 font-bold">{renderStars(deviceObj.Stars)}</span>
                        </div>
                        <div className="text-sm text-gray-700 italic leading-relaxed font-lora line-clamp-4">
                          {decodeMojibake(deviceObj.Description)}
                        </div>
                      </>
                    )}
                  </div>
                );
              })()}
              {(() => {
                const hideoutObj = currentCase.components.hideouts.find(h => h.Name === currentCase.solution.hideout);
                return (
                  <div className="solution-hideout-win bg-white bg-opacity-95 rounded-lg p-3 border-2 border-gray-300 shadow-lg text-left">

                    <div className="text-base font-oswald font-bold text-gray-900 mb-2 normal-case">Hideout: {currentCase.solution.hideout}</div>
                    {hideoutObj && (
                      <>
                        <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                          <span className="flex items-center gap-1">
                            <span>{getAffinityEmoji(hideoutObj.Affinity)}</span>
                            <span className="font-oswald font-semibold">{hideoutObj.Affinity}</span>
                          </span>
                          <span className="text-yellow-600 font-bold">{renderStars(hideoutObj.Stars)}</span>
                        </div>
                        <div className="text-sm text-gray-700 italic leading-relaxed font-lora line-clamp-4">
                          {decodeMojibake(hideoutObj.Description)}
                        </div>
                      </>
                    )}
                  </div>
                );
              })()}
            </div>
            </div>
            
            {/* Desktop buttons at bottom */}
            <div className="hidden sm:flex gap-2 justify-center mt-3 pt-3 border-t border-gray-200">
              <button
                className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-semibold shadow font-oswald"
                onClick={() => {
                  setShowWinModal(false);
                  // Scroll to the top of the page after a short delay to ensure modal is closed
                  setTimeout(() => {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }, 100);
                }}
              >
                CLOSE
              </button>
            </div>
          </div>
        </div>
      )}

      {showLoseModal && currentCase && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
          <div className="bg-white bg-opacity-95 rounded-2xl shadow-2xl p-8 max-w-lg w-full text-center relative max-h-[90vh] overflow-y-auto border-2 border-gray-300">
                            <h2 className="text-2xl font-oswald font-bold text-gray-900 mb-4">GAME OVER - SOLUTION REVEALED</h2>
            <p className="mb-4 text-gray-800 font-lora">
              {newGameMode && proposals.length > 0 ? (
                <>
                  Your proposal of <strong>{proposals[proposals.length - 1].proposal.being}</strong>, <strong>{proposals[proposals.length - 1].proposal.device}</strong>, and <strong>{proposals[proposals.length - 1].proposal.hideout}</strong> was not correct.<br />
                  The solution is shown below:
                </>
              ) : (
                <>
              You've reached the maximum number of moves.<br />
              The solution was:
                </>
              )}
            </p>
            <div className="flex flex-col gap-3 mb-4">
              {(() => {
                const beingObj = currentCase.components.beings.find(b => b.Name === currentCase.solution.being);
                return (
                  <div className="solution-being-lose bg-white bg-opacity-95 rounded-lg p-1.5 border-2 border-gray-300 shadow-lg text-left">
                    <div className="text-sm font-oswald text-gray-900 mb-1 normal-case">Being: {currentCase.solution.being}</div>
                    {beingObj && (
                      <>
                        <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                          <span className="flex items-center gap-1">
                            <span>{getAffinityEmoji(beingObj.Affinity)}</span>
                            <span className="font-oswald">{beingObj.Affinity}</span>
                          </span>
                          <span className="text-yellow-600 font-semibold">{renderStars(beingObj.Stars)}</span>
                        </div>
                        <div className="text-xs text-gray-700 italic leading-tight font-lora">
                          {beingObj.Description}
                        </div>
                      </>
                    )}
                  </div>
                );
              })()}
              {(() => {
                const deviceObj = currentCase.components.devices.find(d => d.Name === currentCase.solution.device);
                return (
                  <div className="solution-weapon-lose bg-white bg-opacity-95 rounded-lg p-1.5 border-2 border-gray-300 shadow-lg text-left">
                    <div className="text-sm font-oswald text-gray-900 mb-1 normal-case">Weapon: {currentCase.solution.device}</div>
                    {deviceObj && (
                      <>
                        <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                          <span className="flex items-center gap-1">
                            <span>{getAffinityEmoji(deviceObj.Affinity)}</span>
                            <span className="font-oswald">{deviceObj.Affinity}</span>
                          </span>
                          <span className="text-yellow-600 font-semibold">{renderStars(deviceObj.Stars)}</span>
                        </div>
                        <div className="text-xs text-gray-700 italic leading-tight font-lora">
                          {deviceObj.Description}
                        </div>
                      </>
                    )}
                  </div>
                );
              })()}
              {(() => {
                const hideoutObj = currentCase.components.hideouts.find(h => h.Name === currentCase.solution.hideout);
                return (
                  <div className="solution-hideout-lose bg-white bg-opacity-95 rounded-lg p-1.5 border-2 border-gray-300 shadow-lg text-left">
                    <div className="text-sm font-oswald text-gray-900 mb-1 normal-case">Hideout: {currentCase.solution.hideout}</div>
                    {hideoutObj && (
                      <>
                        <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                          <span className="flex items-center gap-1">
                            <span>{getAffinityEmoji(hideoutObj.Affinity)}</span>
                            <span className="font-oswald">{hideoutObj.Affinity}</span>
                          </span>
                          <span className="text-yellow-600 font-semibold">{renderStars(hideoutObj.Stars)}</span>
                        </div>
                        <div className="text-xs text-gray-700 italic leading-tight font-lora">
                          {hideoutObj.Description}
                        </div>
                      </>
                    )}
                  </div>
                );
              })()}
            </div>
            
            <button
              className={`mt-4 px-6 py-2 text-white rounded-lg font-semibold shadow font-oswald ${getColorScheme().primary}`}
              onClick={() => {
                setShowLoseModal(false);
                // Scroll to the top of the page after a short delay to ensure modal is closed
                setTimeout(() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }, 100);
              }}
            >
              CLOSE
            </button>
          </div>
        </div>
      )}

      {/* Always visible closing message */}
      <div className="max-w-xl mx-auto">
        <div className="text-center -mt-2 p-4 bg-gray-100 rounded-lg border border-gray-300">
          <p className="text-lg font-oswald font-semibold text-gray-700">
            Solve today's case. Seal the breach. Return tomorrow. The flux never stops.
          </p>
        </div>
      </div>

      {/* Game Mode and Color Scheme Toggles - hidden for now */}
      {/* <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2">
        <button
          onClick={() => setNewGameMode(!newGameMode)}
          className={`px-4 py-3 rounded-lg font-bold text-sm shadow-2xl border-2 border-white transition-colors ${
            newGameMode 
              ? 'bg-red-600 hover:bg-red-700 text-white' 
              : 'bg-green-600 hover:bg-green-700 text-white'
          }`}
        >
          {newGameMode ? '🎯 Single Attempt' : '🎮 Standard Mode'}
        </button>
        <button
          onClick={() => setColorScheme((colorScheme + 1) % 3)}
          className="px-4 py-3 bg-black hover:bg-gray-800 text-white rounded-lg font-bold text-sm shadow-2xl border-2 border-white"
        >
          🎨 {colorScheme === 0 ? 'Gray' : colorScheme === 1 ? 'Purple' : 'Navy'}
        </button>
      </div> */}


      {!isBetaLayout && isAdminMode && (
        <div className="text-center mt-6">
          <Link
            to="/beta"
            className="inline-flex items-center justify-center px-4 py-2 rounded-full font-oswald text-sm sm:text-base bg-white/90 text-slate-800 hover:bg-white transition-colors shadow-lg border border-white uppercase tracking-wide"
          >
            Try the Mobile Beta Interface
          </Link>
        </div>
      )}

      {/* Social Links */}
      <div className="text-center mt-2 mb-2">
        <a href="https://x.com/Dimension_Flux" target="_blank" rel="noopener noreferrer" className="inline-block mx-2 text-white hover:opacity-80">
          {/* X logo */}
          <svg className="w-5 h-5 inline-block align-middle" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M18.244 2H21L13.5 10.5 22.5 22H15.5L10.5 15.5 4.5 22H2L9.5 13.5 1.5 2H8.5L13.5 8.5 18.244 2Z"/>
            </svg>
        </a>
        <a href="https://www.tiktok.com/@dimension_flux" target="_blank" rel="noopener noreferrer" className="inline-block mx-2 text-white hover:opacity-80">
          {/* TikTok/Instagram-style icon (use simple camera glyph as placeholder) */}
          <img src={require('./TikTok_Icon_Black_Circle.png')} alt="TikTok" className="w-5 h-5 inline-block align-middle rounded-full" />
        </a>
      </div>

      {/* View Toggle Buttons (admin only) */}
      {isAdminMode && (
      <div className="text-center mt-2 mb-2 flex justify-center gap-2">
        <button
          onClick={() => setShowClassifiedView(prev => !prev)}
          className="px-4 py-2 rounded text-sm font-semibold bg-gray-200 hover:bg-gray-300 text-gray-800 transition-colors"
          aria-label="Toggle view"
        >
          {showClassifiedView ? 'Standard View' : 'Dossier View'}
        </button>
      </div>
      )}

      {/* Copyright + AI Disclosure */}
      <div className="text-center mt-2 mb-4 text-sm text-white">
        © N.M.A. Pitman 2025 • Contact: <a href="mailto:hello@dimensionflux.com" className="text-white underline">hello@dimensionflux.com</a>
        <div className="mt-1 text-xs opacity-90"><a href="https://www.vecteezy.com/free-vector/portal">Portal Vectors by Vecteezy</a></div>
      </div>



      </div>
      <ConfirmationDialog />
    </div>
    </>
  );
}

function App() {
  // Initialize analytics on app load
  useEffect(() => {
    if (ANALYTICS_CONFIG.ENABLED) {
      analytics.initialize(ANALYTICS_CONFIG.GA_MEASUREMENT_ID);
      
      // Track daily active user
      analytics.trackDailyActive();
      
      // Track page view
      analytics.trackPageView(window.location.pathname);
    }
    
    // Track server-side analytics (complements GA4, works even with cookie blockers)
    // Note: RouteTracker component will handle route change tracking
    serverAnalytics.trackPageView(window.location.pathname);
  }, []);

  // Cookie consent handlers
  const handleCookieAccept = () => {
    // Update Google Analytics consent
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted'
      });
    }
  };

  const handleCookieDecline = () => {
    // Keep analytics storage denied (already set in default consent)
    console.log('User declined cookie consent');
  };

  return (
    <Router>
      <RouteTracker />
      <Routes>
        <Route path="/" element={<MainGame />} />
        <Route path="/beta" element={<MainGame layout="beta" />} />
        <Route path="/getting-started" element={<GettingStarted />} />
        <Route path="/book" element={<Book />} />
        <Route path="/about" element={<About />} />
        <Route path="/stats" element={<Stats />} />
        <Route path="/archive" element={<Archive />} />
        <Route path="/archive/:yearMonth" element={<Archive />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/cookies" element={<Cookies />} />
        <Route path="/admin/analytics" element={<AdminAnalytics />} />
        {/* Removed ResetPassword route - no longer needed */}
      </Routes>
      <CookieConsent 
        onAccept={handleCookieAccept}
        onDecline={handleCookieDecline}
      />
    </Router>
  );
}

export default App;